/**
 * Sprints360Context — estado compartilhado em memória (UX-first).
 * Todas as mutações passam por dispatch(action). Sem persistência ainda.
 *
 * Phase 8.12 — Versionamento de cronograma
 * -----------------------------------------
 * Cada projeto novo cria automaticamente uma versão inicial "v1" (rascunho).
 * Sprints, etapas, negócios e atividades ganham `versao_id`. `projetoData()`
 * filtra pela versão ativa por padrão. Projetos legados (do mock, sem
 * `versoes[]`) continuam a funcionar como antes — o filtro só é aplicado
 * quando o projeto tem ≥ 1 versão registrada.
 *
 * Regras de finalização:
 * - Só 1 versão finalizada por projeto por vez.
 * - Ao finalizar uma nova versão, a anteriormente finalizada (se houver)
 *   é renomeada para "Old vN — DD/MM/YYYY" e marcada como `arquivada=true`.
 *   Continua acessível para cópia futura.
 */
import { createContext, useContext, useReducer, useMemo, useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  PROJETOS_INICIAIS, SPRINTS_INICIAIS, ETAPAS_INICIAIS,
  NEGOCIOS_INICIAIS, ATIVIDADES_INICIAIS,
  calcDataFim, proximoDiaUtil,
} from "@/data/sprints360MockData";
import { hydrateSprints360FromBackend, sprints360Api, mapStatusUiToBackend, getWritesInFlight, fetchAtividadesDoProjetoComDeps } from "@/lib/sprints360Api";
import { markProjetoDirty } from "@/lib/s360MotorState";
import { scopeWorkspaceId } from "@/lib/impersonation";
import { toast } from "sonner";

// Phase 12.72 — Aviso persistente ao usuário em caso de falha do write-through.
// Antes disso, toda falha de sincronização com o backend caía em `console.warn`
// silencioso; o usuário achava que havia salvo mas o dado nunca ia ao banco.
// Agora um toast único é exibido (throttled 5s) informando o problema, e a
// severidade é decidida pelo status HTTP retornado.
let _wtErrorToastAt = 0;
function warnWriteThroughError(scope, err) {
  const status = err?.response?.status;
  const msg = err?.response?.data?.detail || err?.message || String(err);
  console.warn(`[write-through] ${scope}:`, msg, status ? `(HTTP ${status})` : "");
  const now = Date.now();
  if (now - _wtErrorToastAt < 5000) return; // throttle 5s
  _wtErrorToastAt = now;
  const auth = status === 401 || status === 403;
  // Caso específico (S360-CONSULTING): projeto rejeitado por falta de empresa cliente ativa.
  // Antes caía no genérico "não foi possível salvar" — agora vira aviso acionável.
  const semEmpresaCliente = status === 400 &&
    /empresa cliente|empresa_simulada/i.test(msg);
  if (semEmpresaCliente) {
    toast.error("Ative uma empresa cliente para salvar no servidor", {
      description: "Seus dados estão só neste navegador. Escolha/ative uma empresa cliente em " +
        "S360 → Empresas antes de criar o projeto, para que ele seja gravado no banco.",
      duration: 10000,
    });
    return;
  }
  toast.error(
    auth ? "Sessão expirou — faça login novamente para salvar alterações"
         : "Não foi possível salvar no servidor",
    {
      description: auth
        ? "As alterações estão guardadas no seu navegador, mas ainda não foram para o banco."
        : `Suas alterações continuam na tela, mas o servidor não confirmou o salvamento (${scope}). Verifique sua conexão e recarregue a página.`,
      duration: 8000,
    }
  );
}

const Sprints360Ctx = createContext(null);

// Phase 12.4 — Estado limpo (default para clientes reais).
const EMPTY_STATE = {
  projetos:   [],
  versoes:    [],
  sprints:    [],
  etapas:     [],
  negocios:   [],
  atividades: [],
  atividade_historico: [],
  rituais:    [],
};

// Estado com dados-persona (usado apenas pelo bucket "mock" — perfis DEMO
// de teste sem workspace real, para preservar o showcase histórico).
const MOCK_STATE = {
  projetos:   PROJETOS_INICIAIS,
  versoes:    [],
  sprints:    SPRINTS_INICIAIS,
  etapas:     ETAPAS_INICIAIS,
  negocios:   NEGOCIOS_INICIAIS,
  atividades: ATIVIDADES_INICIAIS,
  atividade_historico: [],
  rituais:    [],
};

const initialState = EMPTY_STATE;

const genId = (pref) => `${pref}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

// SB-06 — campos que geram entrada em atividade_historico ao serem alterados.
// Duplica intencionalmente a lista de sb06Params para evitar dependência circular.
const CAMPOS_HISTORICO_LOCAL = [
  "titulo", "descricao",
  "responsavel_id", "responsavel_nome", "profissional_nome",
  "data_inicio_prevista", "data_fim_prevista",
  "data_inicio_real", "data_fim_real",
  "data_inicio", "data_fim",
  "horas_estimadas", "horas_realizadas",
  "status", "percentual_concluido",
  "prioridade", "tags",
  "sprint_id", "projeto_id",
];
// Out/2026 — "numero" (posição "#") NÃO entra nesta lista: não passa pelo
// patch genérico de `updateAtividadeSB06`. Tem fluxo próprio e síncrono
// (`reordenarAtividade`), que já grava seu próprio histórico — ver nota lá.

function reducer(state, a) {
  switch (a.type) {
    case "PROJETO_ADD":    return { ...state, projetos: [a.payload, ...state.projetos] };
    case "PROJETO_UPDATE": return { ...state, projetos: state.projetos.map(p => p.id === a.payload.id ? { ...p, ...a.payload } : p) };
    case "PROJETO_DEL":    return {
      ...state,
      projetos:   state.projetos.filter(p => p.id !== a.id),
      versoes:    state.versoes.filter(v => v.projeto_id !== a.id),
      sprints:    state.sprints.filter(s => s.projeto_id !== a.id),
      etapas:     state.etapas.filter(e => e.projeto_id !== a.id),
      negocios:   state.negocios.filter(n => n.projeto_id !== a.id),
      atividades: state.atividades.filter(t => t.projeto_id !== a.id),
    };

    // Phase 8.12 — Versões
    case "VERSAO_ADD":     return { ...state, versoes: [...state.versoes, a.payload] };
    case "VERSAO_UPDATE":  return { ...state, versoes: state.versoes.map(v => v.id === a.payload.id ? { ...v, ...a.payload } : v) };
    case "VERSAO_DEL":     return { ...state, versoes: state.versoes.filter(v => v.id !== a.id) };
    case "VERSOES_BULK":   return { ...state, versoes: a.payload };  // update em lote (finalização)

    case "SPRINT_ADD":     return { ...state, sprints: [...state.sprints, a.payload] };
    case "SPRINT_UPDATE":  return { ...state, sprints: state.sprints.map(s => s.id === a.payload.id ? { ...s, ...a.payload } : s) };
    case "SPRINT_DEL":     return { ...state, sprints: state.sprints.filter(s => s.id !== a.id) };

    case "ETAPA_ADD":      return { ...state, etapas: [...state.etapas, a.payload] };
    case "ETAPA_UPDATE":   return { ...state, etapas: state.etapas.map(e => e.id === a.payload.id ? { ...e, ...a.payload } : e) };
    case "ETAPA_DEL":      return { ...state, etapas: state.etapas.filter(e => e.id !== a.id) };

    case "NEGOCIO_ADD":    return { ...state, negocios: [...state.negocios, a.payload] };
    case "NEGOCIO_UPDATE": return { ...state, negocios: state.negocios.map(n => n.id === a.payload.id ? { ...n, ...a.payload } : n) };
    case "NEGOCIO_DEL":    return { ...state, negocios: state.negocios.filter(n => n.id !== a.id) };

    // SB-02 — Rituais de Gestão
    case "RITUAL_ADD":     return { ...state, rituais: [...(state.rituais || []), a.payload] };
    case "RITUAL_UPDATE":  return { ...state, rituais: (state.rituais || []).map(r => r.id === a.payload.id ? { ...r, ...a.payload } : r) };
    case "RITUAL_DEL":     return { ...state, rituais: (state.rituais || []).filter(r => r.id !== a.id) };

    case "ATIVIDADE_ADD": {
      // Numero sequencial por projeto+versão (auto).
      let numero = a.payload.numero;
      if (!numero) {
        const escopo = state.atividades.filter(t =>
          t.projeto_id === a.payload.projeto_id
          && (a.payload.versao_id ? t.versao_id === a.payload.versao_id : !t.versao_id));
        const maxNum = escopo.reduce((mx, t) => Math.max(mx, t.numero || 0), 0);
        numero = maxNum + 1;
      }
      return { ...state, atividades: [...state.atividades, { ...a.payload, numero }] };
    }
    case "ATIVIDADE_UPDATE": return { ...state, atividades: state.atividades.map(t => t.id === a.payload.id ? { ...t, ...a.payload } : t) };
    case "ATIVIDADE_DEL":    return { ...state, atividades: state.atividades.filter(t => t.id !== a.id) };
    // Out/2026 — Reposicionamento manual da coluna "#". `a.payload` é a
    // lista de atividades REALMENTE afetadas, já com o `ordem_sequencial`
    // final confirmado pelo backend (resposta do endpoint dedicado
    // `POST /atividades/{id}/reordenar`). Aplica direto, sem recalcular
    // nada no cliente — ver `reordenarAtividade`.
    case "ATIVIDADE_REORDER_BULK": {
      const porId = new Map(a.payload.map(p => [p.id, p]));
      return {
        ...state,
        atividades: state.atividades.map(t => porId.has(t.id) ? { ...t, ...porId.get(t.id) } : t),
      };
    }

    // SB-06 — Histórico granular de alterações da atividade
    case "ATIVIDADE_HIST_ADD":     return { ...state, atividade_historico: [a.payload, ...state.atividade_historico] };
    case "ATIVIDADE_HIST_BULK":    return { ...state, atividade_historico: [...a.payload, ...state.atividade_historico] };

    // Adiciona várias entidades de uma vez (usado ao clonar versão)
    case "BULK_ADD_CLONE":  return {
      ...state,
      sprints:    [...state.sprints,    ...(a.payload.sprints    || [])],
      etapas:     [...state.etapas,     ...(a.payload.etapas     || [])],
      negocios:   [...state.negocios,   ...(a.payload.negocios   || [])],
      atividades: [...state.atividades, ...(a.payload.atividades || [])],
    };

    // Phase 12.9 · Reconcilia id local → id backend após write-through.
    // Payload: { tipo: "atividade"|"projeto"|"versao"|"sprint"|"etapa"|"negocio", id_local, id_backend, patch? }
    case "RECONCILE_BACKEND_ID": {
      const { tipo, id_local, id_backend, patch = {} } = a.payload || {};
      if (!id_local || !id_backend) return state;
      // Jun/2026 — (a) GUARD ANTI-COLAPSO.
      // O `map` abaixo renomeia id_local → id_backend. Se JÁ existir outro
      // item com `id_backend`, ficávamos com duas entradas de mesmo id e o
      // `mergeById` da hidratação seguinte (Map chaveado por id) colapsava as
      // duas silenciosamente — foi assim que um projeto inteiro do usuário foi
      // absorvido por outro (ver /app/memory/forense_projeto_absorvido_*.md).
      // Agora abortamos o reconcile e deixamos o item local intacto e visível.
      const colecaoPorTipo = {
        projeto: "projetos", versao: "versoes", sprint: "sprints",
        etapa: "etapas", negocio: "negocios", atividade: "atividades",
      };
      const col = colecaoPorTipo[tipo];
      if (col && (state[col] || []).some(x => x.id === id_backend && x.id !== id_local)) {
        console.error(
          `[reconcile] ABORTADO · ${tipo} ${id_local} → ${id_backend}: ` +
          `o id de destino já pertence a outro registro. Colapso evitado.`
        );
        return state;
      }
      if (tipo === "atividade") {
        return {
          ...state,
          atividades: state.atividades.map(x => {
            if (x.id === id_local) {
              return { ...x, id: id_backend, _origem: "backend", _prevIds: [...(x._prevIds || []), id_local], ...patch };
            }
            // Bugfix (Ago/2026): outras atividades que já referenciam esta
            // (como predecessora, via `depende_de`) precisam trocar o id
            // local pelo id_backend agora — senão a referência fica
            // "órfã" (apontando para um id que não existe mais) e a
            // dependência nunca é persistida no backend.
            if (Array.isArray(x.depende_de) && x.depende_de.includes(id_local)) {
              return { ...x, depende_de: x.depende_de.map(d => d === id_local ? id_backend : d) };
            }
            return x;
          }),
        };
      }
      if (tipo === "projeto") {
        return {
          ...state,
          projetos:   state.projetos  .map(x => x.id === id_local ? { ...x, id: id_backend, _origem: "backend", _prevIds: [...(x._prevIds || []), id_local], ...patch } : x),
          versoes:    state.versoes   .map(x => x.projeto_id === id_local ? { ...x, projeto_id: id_backend } : x),
          sprints:    state.sprints   .map(x => x.projeto_id === id_local ? { ...x, projeto_id: id_backend } : x),
          etapas:     state.etapas    .map(x => x.projeto_id === id_local ? { ...x, projeto_id: id_backend } : x),
          negocios:   state.negocios  .map(x => x.projeto_id === id_local ? { ...x, projeto_id: id_backend } : x),
          atividades: state.atividades.map(x => x.projeto_id === id_local ? { ...x, projeto_id: id_backend } : x),
          // Phase 12.55.7 — Rituais também precisam ser religados. Antes,
          // ao promover o projeto ao backend, os rituais permaneciam com
          // projeto_id apontando para o id local (prj-...), sumindo do
          // calendário quando o filtro passava a usar o UUID backend.
          rituais:    (state.rituais || []).map(x => x.projeto_id === id_local ? { ...x, projeto_id: id_backend } : x),
        };
      }
      // Phase 12.20 — Reconciliação de versão/sprint/etapa/negócio.
      // Ao reconciliar uma VERSÃO, também religamos suas filhas (sprints, etapas,
      // negocios, atividades) que apontavam para o id local.
      if (tipo === "versao") {
        return {
          ...state,
          versoes:    state.versoes   .map(x => x.id === id_local ? { ...x, id: id_backend, _origem: "backend", ...patch } : x),
          sprints:    state.sprints   .map(x => x.versao_id === id_local ? { ...x, versao_id: id_backend } : x),
          etapas:     state.etapas    .map(x => x.versao_id === id_local ? { ...x, versao_id: id_backend } : x),
          negocios:   state.negocios  .map(x => x.versao_id === id_local ? { ...x, versao_id: id_backend } : x),
          atividades: state.atividades.map(x => x.versao_id === id_local ? { ...x, versao_id: id_backend } : x),
          // Phase 12.55.7 — Rituais também guardam versao_id.
          rituais:    (state.rituais || []).map(x => x.versao_id === id_local ? { ...x, versao_id: id_backend } : x),
          projetos:   state.projetos  .map(x => x.versao_ativa_id === id_local ? { ...x, versao_ativa_id: id_backend } : x),
        };
      }
      if (tipo === "sprint") {
        return {
          ...state,
          sprints:    state.sprints   .map(x => x.id === id_local ? { ...x, id: id_backend, _origem: "backend", ...patch } : x),
          atividades: state.atividades.map(x => x.sprint_id === id_local ? { ...x, sprint_id: id_backend } : x),
        };
      }
      if (tipo === "etapa") {
        return {
          ...state,
          etapas:     state.etapas    .map(x => x.id === id_local ? { ...x, id: id_backend, _origem: "backend", ...patch } : x),
          atividades: state.atividades.map(x => x.etapa_id === id_local ? { ...x, etapa_id: id_backend } : x),
        };
      }
      if (tipo === "negocio") {
        return {
          ...state,
          negocios:   state.negocios  .map(x => x.id === id_local ? { ...x, id: id_backend, _origem: "backend", ...patch } : x),
          atividades: state.atividades.map(x => x.negocio_id === id_local ? { ...x, negocio_id: id_backend } : x),
        };
      }
      return state;
    }

    default: return state;
  }
}

// Phase 12.4 — Substitui todo o estado (usado ao trocar de tenant/sessão).
// Phase 12.9 — Fase adicional __MERGE_HYDRATED__ para agrupar dados vindos
// do backend com o que já existe em localStorage. Merge por `id` sem
// sobrescrever alterações locais recentes (backend perde para local se
// updated_at local for mais novo, mas como não temos versionamento local
// ainda, a política atual é "backend gana se _origem local==backend" e
// "local gana se _origem local==front-only").
//
// Phase 12.72 — Correção do bug residual do write-through:
// Se `cur._origem === "backend"` MAS `cur.updated_at > rem.updated_at`
// (edição local recente ainda não refletida no snapshot do backend por
// causa do PATCH in-flight), preservamos os campos locais para não haver
// "regressão" após F5. O backend gana só quando é realmente mais recente.
function replaceReducer(state, a) {
  if (a.type === "__REPLACE_STATE__") return a.payload;
  if (a.type === "__MERGE_HYDRATED__") {
    const payload = a.payload || {};
    const isNewer = (localVal, remoteVal) => {
      if (!localVal) return false;
      if (!remoteVal) return true;
      try {
        return new Date(localVal).getTime() > new Date(remoteVal).getTime();
      } catch { return false; }
    };
    const mergeById = (localArr, remoteArr, unionFields = []) => {
      const localMap = new Map((localArr || []).map(x => [x.id, x]));
      for (const rem of (remoteArr || [])) {
        const cur = localMap.get(rem.id);
        if (!cur) {
          // Item novo do backend — inserir
          localMap.set(rem.id, rem);
        } else if (cur._origem === "backend") {
          // Já veio do backend antes. Compara updated_at para decidir quem gana:
          //  - local mais novo → preserva local (write-through ainda em flight)
          //  - backend mais novo → aplica backend
          if (isNewer(cur.updated_at, rem.updated_at)) {
            // preserva local; ainda assim ancora campos do backend que faltem
            localMap.set(rem.id, { ...rem, ...cur });
          } else {
            const merged = { ...cur, ...rem };
            // Bugfix (Ago/2026): campos em `unionFields` (ex.: `depende_de`)
            // vivem numa tabela separada, escrita via POST fire-and-forget
            // DESACOPLADO do `updated_at` da própria atividade. Um hydrate
            // cujo snapshot do backend foi lido antes dessa escrita comitar
            // "ganha" a atividade (seu updated_at é mais novo) mas carrega
            // um valor desatualizado nesses campos — perdendo, por exemplo,
            // a predecessora que o usuário tinha acabado de adicionar. Por
            // isso fazemos UNION em vez de sobrescrever puro.
            for (const campo of unionFields) {
              const a = Array.isArray(cur[campo]) ? cur[campo] : [];
              const b = Array.isArray(rem[campo]) ? rem[campo] : [];
              merged[campo] = Array.from(new Set([...a, ...b]));
            }
            localMap.set(rem.id, merged);
          }
        } else {
          // Item foi criado só no front → preserva o local (write-through
          // futuro sincroniza).
        }
      }
      return Array.from(localMap.values());
    };
    return {
      ...state,
      projetos:      mergeById(state.projetos,      payload.projetos),
      atividades:    mergeById(state.atividades,    payload.atividades, ["depende_de"]),
      // Phase 12.20 — write-through das entidades filhas
      versoes:       mergeById(state.versoes,       payload.versoes),
      sprints:       mergeById(state.sprints,       payload.sprints),
      etapas:        mergeById(state.etapas,        payload.etapas),
      negocios:      mergeById(state.negocios,      payload.negocios),
      // participantes/rituais do backend não entram no reducer local hoje
      // (o `AbaParticipantes` usa `users_db` mock). Guardamos em campos
      // "shadow" para consulta futura via helper `getParticipantesBackend`.
      participantes_backend: payload.participantes ?? state.participantes_backend ?? [],
      rituais_backend:       payload.rituais       ?? state.rituais_backend       ?? [],
    };
  }
  return reducer(state, a);
}

export function Sprints360Provider({ children }) {
  const location = useLocation();
  // Persistência local do estado, ISOLADA POR TENANT (Phase 12.4).
  //
  // Antes de Phase 12.4 usávamos uma única chave `s360_state_v1` compartilhada
  // entre todos os workspaces do mesmo browser — o que fazia dados de um
  // cliente vazarem no dashboard de outro cliente no mesmo dispositivo.
  //
  // Agora derivamos o bucket a partir do `app_session`:
  //   • Com sessão real  → `s360_state_v1__<workspace_id>`  (estado vazio ao 1º login)
  //   • Sem sessão / DEMO → `s360_state_v1__mock`            (seed histórico)
  const LS_PREFIX = "s360_state_v1";
  const MOCK_BUCKET = `${LS_PREFIX}__mock`;

  // Jun/2026 — Em modo ghost (ou Super Admin dentro de uma empresa pelo V360)
  // o `app_session` não tem workspace_id, o que jogava o bucket no MOCK e
  // fazia o S360 exibir os projetos DEMO (Vector Logística) em vez dos do
  // cliente. `scopeWorkspaceId()` centraliza a precedência.
  const getScopeWorkspaceId = useCallback(() => scopeWorkspaceId(), []);
  void getScopeWorkspaceId;  // mantido para compat de leitura; ver getStableScopeWorkspaceId

  // Jun/2026 — (e) ESTABILIDADE DE BUCKET.
  // Bug: durante uma renovação de token, um `SIGNED_OUT` espúrio ou o bootstrap
  // do `initSessionBridge` num F5, o `app_session` fica momentaneamente ausente
  // → `scopeWorkspaceId()` devolvia null → a chave virava `__mock` → o listener
  // `rehydrate` substituía TODO o estado pelo bucket mock (vazio) e o efeito de
  // persistência gravava estado vazio por cima. Resultado: "os projetos
  // sumiram" e o que fosse criado nessa janela ia para o bucket errado.
  // Agora: uma vez que um workspace real foi visto, jamais caímos no mock.
  const ultimoScopeRealRef = useRef(null);
  const getStableScopeWorkspaceId = useCallback(() => {
    const scope = scopeWorkspaceId();
    if (scope) {
      ultimoScopeRealRef.current = scope;
      return scope;
    }
    // Sem escopo agora: se ainda existe `app_session` (ou já vimos um tenant
    // real neste ciclo de vida da página), mantemos o último bucket real.
    let temSessao = false;
    try { temSessao = !!window.localStorage.getItem("app_session"); } catch { /* noop */ }
    if (ultimoScopeRealRef.current && temSessao) return ultimoScopeRealRef.current;
    return null;
  }, []);

  const getBucketKey = useCallback(() => {
    const scope = getStableScopeWorkspaceId();
    return scope ? `${LS_PREFIX}__${scope}` : MOCK_BUCKET;
  }, [getStableScopeWorkspaceId]);

  // Phase 12.28 — Marcador de workspace no snapshot. Escrevemos junto com o
  // state e validamos na leitura. Se o `_wsId` gravado não bater com o
  // workspace_id da sessão corrente (ex.: chave manipulada, bucket órfão
  // de sessão anterior no mesmo device), descartamos o cache e retornamos
  // EMPTY_STATE para evitar leak cross-tenant.
  const getCurrentWorkspaceId = useCallback(() => getStableScopeWorkspaceId(), [getStableScopeWorkspaceId]);

  const readBucket = useCallback((key) => {
    try {
      const raw = typeof window !== "undefined" ? window.localStorage.getItem(key) : null;
      if (!raw) {
        // Bucket "mock" (usuário DEMO/sem sessão) inicia com PROJETOS_INICIAIS
        // etc. Qualquer bucket real de cliente parte do EMPTY_STATE.
        return key === MOCK_BUCKET ? MOCK_STATE : EMPTY_STATE;
      }
      const parsed = JSON.parse(raw);
      // Guard cross-tenant: só aplicável a buckets reais (não ao MOCK).
      if (key !== MOCK_BUCKET) {
        const currentWs = getCurrentWorkspaceId();
        // Jun/2026 — o workspace também está codificado no SUFIXO da chave.
        // Antes, um snapshot sem `_wsId` (gravado numa janela em que o escopo
        // estava indisponível) era descartado inteiro — perda de dados. Agora
        // o sufixo da chave serve de fonte alternativa.
        const wsDaChave = key.startsWith(`${LS_PREFIX}__`) ? key.slice(`${LS_PREFIX}__`.length) : null;
        const storedWs = parsed?._wsId || wsDaChave || null;
        if (!storedWs || (currentWs && storedWs !== currentWs)) {
          return EMPTY_STATE;
        }
      }
      // Merge defensivo — se algum campo faltar, mantém do initialState.
      return {
        projetos:            Array.isArray(parsed.projetos)            ? parsed.projetos            : initialState.projetos,
        versoes:             Array.isArray(parsed.versoes)             ? parsed.versoes             : initialState.versoes,
        sprints:             Array.isArray(parsed.sprints)             ? parsed.sprints             : initialState.sprints,
        etapas:              Array.isArray(parsed.etapas)              ? parsed.etapas              : initialState.etapas,
        negocios:            Array.isArray(parsed.negocios)            ? parsed.negocios            : initialState.negocios,
        atividades:          Array.isArray(parsed.atividades)          ? parsed.atividades          : initialState.atividades,
        atividade_historico: Array.isArray(parsed.atividade_historico) ? parsed.atividade_historico : [],
        rituais:             Array.isArray(parsed.rituais)             ? parsed.rituais             : [],
      };
    } catch {
      return key === MOCK_BUCKET ? MOCK_STATE : EMPTY_STATE;
    }
  }, [getCurrentWorkspaceId]);

  const initFromStorage = () => readBucket(getBucketKey());
  const [state, dispatch] = useReducer(replaceReducer, undefined, initFromStorage);

  // Chave atual (tracked para persist + re-hidratar em troca de sessão).
  const bucketRef = useRef(getBucketKey());

  // Escuta trocas de sessão (login/logout/ghost) e re-hidrata do bucket certo.
  useEffect(() => {
    const rehydrate = () => {
      const key = getBucketKey();
      if (key === bucketRef.current) return;
      bucketRef.current = key;
      const next = readBucket(key);
      dispatch({ type: "__REPLACE_STATE__", payload: next });
    };
    window.addEventListener("app-session-changed", rehydrate);
    window.addEventListener("storage", rehydrate);
    return () => {
      window.removeEventListener("app-session-changed", rehydrate);
      window.removeEventListener("storage", rehydrate);
    };
  }, [getBucketKey, readBucket]);

  // Persistir a cada mudança (safe stringify — histórico limitado a 500 entradas para não estourar quota).
  useEffect(() => {
    const key = getBucketKey();
    // Phase 12.28 — Só carimba `_wsId` em buckets reais. O MOCK segue livre.
    const stampWs = key === MOCK_BUCKET ? null : getCurrentWorkspaceId();
    // Jun/2026 — (e) NUNCA gravar estado vazio por cima de um bucket com
    // conteúdo. Era assim que uma janela sem sessão zerava o bucket: o state
    // virava EMPTY_STATE e o efeito o persistia, destruindo o snapshot.
    const vazio = (state.projetos || []).length === 0
      && (state.atividades || []).length === 0
      && (state.versoes || []).length === 0;
    if (vazio) {
      try {
        const anterior = window.localStorage.getItem(key);
        if (anterior) {
          const p = JSON.parse(anterior);
          if ((p?.projetos || []).length > 0 || (p?.atividades || []).length > 0) {
            console.warn("[s360] gravação de estado vazio BLOQUEADA sobre bucket com conteúdo:", key);
            return;
          }
        }
      } catch { /* segue e grava */ }
    }
    try {
      const snap = {
        ...state,
        atividade_historico: (state.atividade_historico || []).slice(0, 500),
        ...(stampWs ? { _wsId: stampWs } : {}),
      };
      window.localStorage.setItem(key, JSON.stringify(snap));
    } catch (err) {
      // Se a quota estourar, tenta gravar sem o histórico
      try {
        window.localStorage.setItem(key, JSON.stringify({
          ...state,
          atividade_historico: [],
          ...(stampWs ? { _wsId: stampWs } : {}),
        }));
      } catch { /* ignore */ }
    }
  }, [state, getBucketKey, getCurrentWorkspaceId]);

  // ---------------------------------------------------------------
  // Phase 12.9 — Hidratação do backend (P0)
  // ---------------------------------------------------------------
  // Ao montar, se houver sessão real (bucket ≠ mock), busca projetos +
  // snapshots do backend e faz MERGE não-destrutivo no state.
  // A ideia é: qualquer projeto criado via API real (ex.: pelo Admin,
  // pelo test_mgc_r14_r17, ou por endpoints diretos) passa a estar
  // visível na UI que hoje só lê do reducer local — desbloqueando
  // Sprints360Projeto, MGC, Gantt etc. para dados reais.
  //
  // Erros são silenciados (mantém o localStorage como fallback).
  const [hydrated, setHydrated] = useState(false);
  const [hydrationError, setHydrationError] = useState(null);
  const retryTimerRef = useRef(null);
  // Fev/2026 — Dedup de chamadas concorrentes. Mount, troca de sessão
  // (`app-session-changed`) e a rede de segurança por rota (linha ~621)
  // podem pedir re-hidratação quase ao mesmo tempo; cada chamada agora
  // custa alguns segundos de trabalho real no banco (snapshot do portfólio
  // inteiro via Item 3) — sem isso, 3 pedidos "simultâneos" viravam 3
  // consultas completas. Uma chamada já em andamento é reaproveitada por
  // quem pedir de novo; só um retry explícito (tentativa>0, agendado após
  // falha) força uma chamada nova.
  const hydratingPromiseRef = useRef(null);
  const hydrateFromBackend = useCallback(async (tentativa = 0) => {
    if (tentativa === 0 && hydratingPromiseRef.current) {
      return hydratingPromiseRef.current;
    }
    const promise = (async () => {
      const key = getBucketKey();
      if (key.endsWith("__mock")) {
        // Sessão DEMO/sem tenant real — não bate no backend.
        setHydrated(true);
        return;
      }
      try {
        const remoto = await hydrateSprints360FromBackend({}, (projetos) => {
          // Publica a lista de projetos assim que o snapshot único (Item 3)
          // responde — já vem com todos os detalhes (atividades, versões,
          // etapas…) na mesma resposta, sem lotes subsequentes.
          dispatch({ type: "__MERGE_HYDRATED__", payload: { projetos } });
          setHydrationError(null);
          setHydrated(true);
        });
        dispatch({ type: "__MERGE_HYDRATED__", payload: remoto });
        setHydrationError(null);
      } catch (err) {
        setHydrationError(err?.message || String(err));
        // Jun/2026 — falha na 1ª hidratação fazia os projetos do backend
        // "desaparecerem" até o próximo login (o usuário só via os que criou
        // na sessão, que vivem no reducer local). Agora reagenda com backoff.
        if (tentativa < 2) {
          clearTimeout(retryTimerRef.current);
          retryTimerRef.current = setTimeout(
            () => hydrateFromBackend(tentativa + 1), 2000 * (tentativa + 1));
        }
      } finally {
        setHydrated(true);
        if (tentativa === 0) hydratingPromiseRef.current = null;
      }
    })();
    if (tentativa === 0) hydratingPromiseRef.current = promise;
    return promise;
  }, [getBucketKey]);

  // Bootstrap inicial + re-hidrata em troca de sessão (login/logout) e em
  // início/fim de modo ghost (`imp-session-changed`), porque o header
  // `X-Imp-Workspace` muda o tenant efetivo das chamadas.
  useEffect(() => {
    hydrateFromBackend();
    const onSessionChanged = () => {
      setHydrated(false);
      hydrateFromBackend();
    };
    window.addEventListener("app-session-changed", onSessionChanged);
    window.addEventListener("imp-session-changed", onSessionChanged);
    return () => {
      window.removeEventListener("app-session-changed", onSessionChanged);
      window.removeEventListener("imp-session-changed", onSessionChanged);
      clearTimeout(retryTimerRef.current);
    };
  }, [hydrateFromBackend]);

  // ---------------------------------------------------------------
  // Phase 12.9 — Promoção de projeto local → backend (backfill)
  // ---------------------------------------------------------------
  // Quando o usuário criou um projeto+atividades ANTES do write-through
  // existir (ou antes de ter sessão real), tudo mora só em localStorage.
  // Este helper "sobe" tudo de uma vez para o backend e reconcilia os
  // ids no state. É idempotente: se o projeto já for backend, retorna
  // o UUID direto sem tocar em nada.
  //
  // Uso principal: S360AcoesDecisoes ao entrar/clicar "Atualizar" —
  // garante que o motor MGC tenha os dados no banco antes de rodar.
  const stateRef = useRef(state);
  useEffect(() => { stateRef.current = state; }, [state]);

  // Jun/2026 — Rede de segurança de apresentação: ao ENTRAR no Sprints360
  // (inclusive vindo do V360), se não há nenhum projeto vindo do backend no
  // state — ou se a última hidratação falhou — tentamos de novo. Antes, uma
  // única falha deixava o módulo aparentemente "sem projetos" até o usuário
  // sair e logar de novo. Cooldown de 30s evita rajada de chamadas.
  const ultimaRehidratacaoRef = useRef(0);
  useEffect(() => {
    if (!hydrated) return;
    if (location.pathname.startsWith("/app/v360")) return;
    const temBackend = (stateRef.current.projetos || []).some(p => p._origem === "backend");
    if (temBackend && !hydrationError) return;
    const agora = Date.now();
    if (agora - ultimaRehidratacaoRef.current < 30000) return;
    ultimaRehidratacaoRef.current = agora;
    hydrateFromBackend();
  }, [location.pathname, hydrated, hydrationError, hydrateFromBackend]);

  // Phase 12.34 — Ref para `promoteProjetoParaBackend`, usada por
  // `createProjeto` (que tem deps `[]`) para disparar promote automático
  // sem virar dependência do useCallback (evitaria recriar a identidade
  // do createProjeto a cada render).
  const promoteProjetoParaBackendRef = useRef(null);

  const isRealSession = useCallback(() => !getBucketKey().endsWith("__mock"), [getBucketKey]);

  // ===============================================================
  // Jun/2026 — (b) FILA DE SINCRONIZAÇÃO
  // ===============================================================
  // Antes, todo write-through desistia em silêncio quando o id ainda era
  // local (`projeto_id` = "prj-…" ou `id` = "ati-…"): um simples `return`,
  // sem retry e sem aviso. Como o promote do projeto roda em `setTimeout` e
  // leva segundos, TUDO que o usuário fizesse nessa janela (importar a grade,
  // aplicar o Simulador, digitar datas) era descartado do banco. Era a causa
  // das atividades sem data (10 de 117 num projeto real).
  //
  // Agora a ação entra numa fila, os ids locais são reescritos para os UUIDs
  // do backend conforme os reconciles chegam, e a ação é reenviada.
  const pendingSyncRef = useRef([]);
  const syncToBackendRef = useRef(null);
  // Guard de concorrência dos promotes — declarado aqui (antes era junto do
  // `promoteProjetoParaBackend`) porque o contador de pendências o consulta.
  const promoteInFlightRef = useRef(new Set());
  // Jun/2026 — "Salvar antes de sair": quantidade de operações que AINDA não
  // estão gravadas no servidor (fila + promotes + escritas em voo).
  const [pendingSyncCount, setPendingSyncCount] = useState(0);
  const contarPendencias = useCallback(
    () => pendingSyncRef.current.length + promoteInFlightRef.current.size + getWritesInFlight(),
    [],
  );
  // A fila vive em localStorage, no MESMO escopo de tenant do state. Antes era
  // só em memória: um F5 ou um logout no meio da sincronização destruía tudo
  // que ainda não tinha subido (foi assim que 5 sprints e 14 atividades se
  // perderam no teste de aceitação).
  const filaKey = useCallback(() => `s360_sync_queue__${getBucketKey().replace(`${LS_PREFIX}__`, "")}`, [getBucketKey]);
  const salvarFila = useCallback(() => {
    try {
      const k = filaKey();
      if (pendingSyncRef.current.length) {
        window.localStorage.setItem(k, JSON.stringify(pendingSyncRef.current));
      } else {
        window.localStorage.removeItem(k);
      }
    } catch { /* quota */ }
  }, [filaKey]);
  const PREFIX_COLECAO = {
    "prj-": "projetos", "ver-": "versoes", "spr-": "sprints",
    "etp-": "etapas", "neg-": "negocios", "ati-": "atividades",
  };
  const ehLocal = (id) => !!id && !!PREFIX_COLECAO[String(id).slice(0, 4)];

  const resolveBackendId = useCallback((colecao, localId) => {
    const arr = stateRef.current[colecao] || [];
    const hit = arr.find(x => x.id === localId || (x._prevIds || []).includes(localId));
    if (!hit || ehLocal(hit.id)) return null;
    return hit.id;
  }, []);

  const enqueueSync = useCallback((action, motivo) => {
    if (pendingSyncRef.current.length >= 500) {
      console.warn("[sync-queue] fila cheia, descartando:", action.type);
      return;
    }
    pendingSyncRef.current.push({ action, tentativas: 0, motivo, em: Date.now() });
    salvarFila();
    setPendingSyncCount(contarPendencias());
    console.log(`[sync-queue] enfileirado ${action.type} (${motivo}) · fila=${pendingSyncRef.current.length}`);
  }, [salvarFila]);

  // Reescreve os ids locais da ação usando o state atual. Devolve null se
  // algum id essencial ainda não foi reconciliado (a ação continua na fila).
  const remapAction = useCallback((action) => {
    const out = { ...action };
    if (out.id && ehLocal(out.id)) {
      const r = resolveBackendId(PREFIX_COLECAO[String(out.id).slice(0, 4)], out.id);
      if (!r) return null;
      out.id = r;
    }
    if (out.payload) {
      const p = { ...out.payload };
      for (const campo of ["id", "projeto_id"]) {
        if (p[campo] && ehLocal(p[campo])) {
          const r = resolveBackendId(PREFIX_COLECAO[String(p[campo]).slice(0, 4)], p[campo]);
          if (!r) return null;
          p[campo] = r;
        }
      }
      // `versao_id` é ESTRUTURAL: mandar null aqui deixava a atividade fora da
      // versão para sempre. Se a versão ainda não foi reconciliada, a ação
      // espera na fila.
      if (p.versao_id && ehLocal(p.versao_id)) {
        const rv = resolveBackendId("versoes", p.versao_id);
        if (!rv) return null;
        p.versao_id = rv;
      }
      // FKs de agrupamento: se ainda locais, viram null e o reconcile de
      // etapa/negócio dispara um PATCH corretivo depois.
      for (const campo of ["etapa_id", "negocio_id", "sprint_id"]) {
        if (p[campo] && ehLocal(p[campo])) {
          p[campo] = resolveBackendId(PREFIX_COLECAO[String(p[campo]).slice(0, 4)], p[campo]) || null;
        }
      }
      // Bugfix (Ago/2026): reescreve ids locais DENTRO do array `depende_de`
      // também — sem isso, uma predecessora ainda local no momento do
      // enfileiramento nunca era resolvida, mesmo depois de reconciliada
      // (o bloco de persistência de dependências só pula ids "ati-…", não
      // há retry específico para eles além deste remap).
      if (Array.isArray(p.depende_de)) {
        p.depende_de = p.depende_de.map(d =>
          d && ehLocal(d) ? (resolveBackendId(PREFIX_COLECAO[String(d).slice(0, 4)], d) || d) : d
        );
      }
      out.payload = p;
    }
    return out;
  }, [resolveBackendId]);

  const drainPendingSync = useCallback(() => {
    if (!pendingSyncRef.current.length) return;
    const fila = pendingSyncRef.current;
    pendingSyncRef.current = [];
    for (const item of fila) {
      const pronta = remapAction(item.action);
      if (!pronta) {
        item.tentativas += 1;
        if (item.tentativas > 60) {
          console.error("[sync-queue] DESCARTADA após 60 tentativas:", item.action.type, item.motivo);
          warnWriteThroughError(item.action.type, new Error("id local nunca foi reconciliado"));
          continue;
        }
        pendingSyncRef.current.push(item);
        continue;
      }
      // Jun/2026 — ANTI-DUPLICAÇÃO. Uma ação `*_ADD` só faz sentido enquanto o
      // objeto ainda não existe no backend. Se o id local dela JÁ resolveu
      // para um UUID, o `promoteProjetoParaBackend` (que sobe versões,
      // sprints, etapas, negócios e atividades) chegou primeiro — reenviar
      // criaria um registro duplicado.
      const idOriginal = item.action?.payload?.id;
      if (item.action.type.endsWith("_ADD") && idOriginal && ehLocal(idOriginal)
          && pronta.payload?.id && !ehLocal(pronta.payload.id)) {
        console.log(`[sync-queue] descartando ${item.action.type} — já criado pelo promote (${pronta.payload.id})`);
        continue;
      }
      console.log(`[sync-queue] reenviando ${pronta.type}`);
      syncToBackendRef.current?.(pronta, { daFila: true });
    }
    salvarFila();
  }, [remapAction, salvarFila]);

  // Recarrega a fila do localStorage ao montar / trocar de tenant.
  // `filaRetomadaRef` garante UMA leitura por chave: sem ele o efeito
  // re-executava e sobrescrevia `pendingSyncRef` com a fila antiga, anulando
  // o progresso do dreno num laço infinito.
  const filaRetomadaRef = useRef(null);
  useEffect(() => {
    try {
      const k = filaKey();
      if (filaRetomadaRef.current === k) return;
      filaRetomadaRef.current = k;
      const raw = window.localStorage.getItem(k);
      if (!raw) return;
      const itens = JSON.parse(raw);
      if (Array.isArray(itens) && itens.length) {
        // Zera o contador: ids que não resolviam antes podem resolver agora
        // que o state foi re-hidratado do backend.
        pendingSyncRef.current = [...pendingSyncRef.current, ...itens.map(i => ({ ...i, tentativas: 0 }))];
        console.log(`[sync-queue] retomando ${itens.length} operação(ões) pendente(s) do armazenamento local`);
      }
    } catch { /* noop */ }
  }, [filaKey]);

  // Dreno periódico enquanto houver fila. Simples e resiliente: cobre tanto o
  // reconcile do promote quanto o do write-through, sem acoplar aos call sites.
  useEffect(() => {
    const t = setInterval(() => {
      if (pendingSyncRef.current.length) drainPendingSync();
      setPendingSyncCount(prev => {
        const atual = contarPendencias();
        return atual === prev ? prev : atual;
      });
    }, 1200);
    return () => clearInterval(t);
  }, [drainPendingSync, contarPendencias]);

  /**
   * Força o dreno da fila e resolve quando NADA mais está pendente
   * (fila vazia + nenhum promote + nenhuma escrita em voo). Usado pelo logout.
   */
  const flushPendingSync = useCallback(async (timeoutMs = 20000) => {
    const t0 = Date.now();
    for (;;) {
      const restantes = contarPendencias();
      setPendingSyncCount(restantes);
      if (restantes === 0) return { ok: true, restantes: 0 };
      if (Date.now() - t0 >= timeoutMs) return { ok: false, restantes };
      if (pendingSyncRef.current.length) drainPendingSync();
      await new Promise(r => setTimeout(r, 500));
    }
  }, [drainPendingSync, contarPendencias]);

  const promoteProjetoParaBackend = useCallback(async (projetoId) => {
    if (!projetoId) return null;
    if (!isRealSession()) return projetoId; // sem sessão real, mantém local
    // (a) Guard de concorrência — dois promotes simultâneos do mesmo projeto
    // criavam duas linhas (ou geravam 409 + reconcile cruzado).
    if (promoteInFlightRef.current.has(projetoId)) {
      console.log(`[promote] já em andamento para ${projetoId} — ignorando chamada duplicada`);
      return projetoId;
    }
    promoteInFlightRef.current.add(projetoId);
    try {
    const cur = stateRef.current.projetos.find(p => p.id === projetoId);
    if (!cur) return projetoId;
    // Já é backend? Nada a fazer.
    if (cur._origem === "backend" && !String(cur.id).startsWith("prj-")) {
      return cur.id;
    }

    // Captura as filhas ANTES de qualquer reconcile — importante porque o
    // dispatch de RECONCILE_BACKEND_ID pode não ter propagado ao stateRef
    // quando formos criar as atividades filhas.
    const filhasLocais = stateRef.current.atividades.filter(a => a.projeto_id === projetoId);
    // Phase 12.60 — Captura ETAPAS e NEGÓCIOS locais ANTES do reconcile,
    // pelo `projeto_id` local (ainda `prj-…`). Antes disso, a filtragem
    // acontecia após o dispatch de RECONCILE (linha ~550) usando
    // `projeto_id === idBackend`, mas o `stateRef` só é sincronizado com o
    // state React após um novo commit, então o filtro devolvia []. Bug:
    // nenhum negócio/etapa era criado no backend → atividades importadas do
    // V360 ficavam com negocio_id/etapa_id = null → todas caíam em
    // "(Sem negócio)" no SB-16 e no detalhe do projeto.
    const etapasLocais = stateRef.current.etapas.filter(
      e => e.projeto_id === projetoId && String(e.id).startsWith("etp-")
    );
    const negociosLocais = stateRef.current.negocios.filter(
      n => n.projeto_id === projetoId && String(n.id).startsWith("neg-")
    );
    console.log(`[promote] "${cur.nome}" (${filhasLocais.length} atividades locais, ${etapasLocais.length} etapas, ${negociosLocais.length} negócios) → backend`);

    // === IDEMPOTÊNCIA ROBUSTA ===
    // Antes de criar, verifica se já existe no backend projeto com mesmo
    // código OU mesmo nome. Se existe, apenas reconcilia (evita a duplicação
    // que aconteceu com o SpaceX — 4 projetos vazios criados a cada visita).
    let idBackend = null;
    try {
      const { projetos: existentes = [] } = await sprints360Api.projetos.listar();
      // Jun/2026 — (a) dedup SOMENTE por `codigo`. O casamento por `nome` era
      // o vetor do colapso: dois projetos com nome igual/quase igual eram
      // tratados como o mesmo registro e o segundo era reconciliado para o
      // UUID do primeiro, herdando etapas/negócios/atividades de ambos.
      const match = cur.codigo
        ? existentes.find(p => p.codigo === cur.codigo)
        : null;
      if (match) {
        console.log(`[promote] IDEMPOTENTE · projeto "${cur.nome}" já existe no backend como ${match.id} → reconciliando sem duplicar`);
        idBackend = match.id;
      }
    } catch (e) {
      console.warn("[promote] falha ao listar projetos p/ dedup:", e?.message || e);
    }

    // 1) Cria projeto no backend (só se não existir)
    if (!idBackend) {
      try {
        const created = await sprints360Api.projetos.criar({
          codigo: cur.codigo,
          nome: cur.nome || "Projeto sem nome",
          tipo_projeto: cur.tipo_projeto || null,
          status: cur.status === "ativo" || cur.status === "planejamento" ? cur.status : "ativo",
          cor: cur.cor || null,
          sponsor_nome: cur.sponsor_nome || null,
          gp_nome: cur.gp_nome || null,
          data_inicio: cur.data_inicio_prevista || null,
          data_fim_prevista: cur.data_fim_prevista || null,
          cadencia_dias: cur.cadencia_dias || 14,
          contexto_livre: cur.contexto_livre || null,
          // Jun/2026 — CORREÇÃO DO ÓRFÃO NO CAMINHO REAL. O `promote` é o
          // CRIADOR ÚNICO (o write-through pula quando `_promoteWillCreate`),
          // mas até agora ele NÃO enviava `empresa_simulada_id`. Em workspace
          // S360-CONSULTING o backend rejeitava com 400 (guard) e o projeto
          // nunca chegava ao Supabase — sobrevivia só no localStorage e sumia
          // em outro dispositivo/limpeza. `createProjeto` já garante o campo no
          // state (fallback p/ 1ª empresa), então aqui basta propagá-lo.
          empresa_simulada_id: cur.empresa_simulada_id || null,
        });
        idBackend = created.id;
      } catch (e) {
        // Recuperação de RACE: se o backend rejeitou por UNIQUE (409),
        // outro promote paralelo já criou este projeto. Re-lista e casa
        // pelo mesmo critério (código ou nome) — evita perda de vínculo.
        const status = e?.response?.status;
        if (status === 409) {
          try {
            const { projetos: agora = [] } = await sprints360Api.projetos.listar();
            // Recuperação de corrida também só por `codigo` (ver nota do dedup).
            const race = cur.codigo ? agora.find(p => p.codigo === cur.codigo) : null;
            if (race) {
              console.log(`[promote] recuperou de 409 · "${cur.nome}" já existia como ${race.id}`);
              idBackend = race.id;
            }
          } catch (e2) {
            console.warn("[promote] falha ao re-listar após 409:", e2?.message || e2);
          }
        }
        if (!idBackend) {
          console.warn("[promote projeto]", cur.nome, e?.message || e);
          return projetoId;
        }
      }
    }

    // 2) Reconcilia projeto (religa filhos no state local). O `patch` devolve
    // o `codigo` do backend para o state — sem isso o dedup por código não
    // funcionaria em promotes futuros.
    dispatch({ type: "RECONCILE_BACKEND_ID", payload: {
      tipo: "projeto", id_local: projetoId, id_backend: idBackend,
      patch: { codigo: cur.codigo },
    }});

    // 2.1) (d) VERSÕES — precisam existir no backend ANTES de etapas,
    // negócios, sprints e atividades, que carregam `versao_id`. Antes deste
    // bloco o promote nunca criava versão: o write-through de VERSAO_ADD
    // desistia (projeto_id ainda "prj-…") e o promote ignorava o assunto.
    // Resultado: `sprints.versoes` ficava com ZERO linhas e o versionamento
    // existia só no localStorage.
    const versaoIdMap = new Map();
    const versoesLocais = stateRef.current.versoes.filter(
      v => v.projeto_id === projetoId && String(v.id).startsWith("ver-")
    );
    for (const v of versoesLocais) {
      try {
        const criada = await sprints360Api.versoes.criar({
          projeto_id: idBackend,
          numero: v.numero || 1,
          rotulo: v.rotulo || `v${v.numero || 1}`,
          descricao: v.descricao || null,
          status: v.status || "rascunho",
        });
        versaoIdMap.set(v.id, criada.id);
        dispatch({ type: "RECONCILE_BACKEND_ID", payload: {
          tipo: "versao", id_local: v.id, id_backend: criada.id,
        }});
      } catch (err) {
        console.warn("[promote versao]", v.rotulo, err?.message || err);
      }
    }
    const resolveVersao = (id) => (id
      ? (versaoIdMap.get(id) || (String(id).startsWith("ver-") ? null : id))
      : null);

    // Phase 12.56 — Promove ETAPAS e NEGÓCIOS ANTES das atividades filhas.
    // Sem isso, as atividades eram criadas no backend com etapa_id/negocio_id
    // = null (pois os IDs locais `etp-…`/`neg-…` são rejeitados pela
    // validação Pydantic). Ao re-hidratar, os campos Etapa/Negócio
    // apareciam em branco na tabela do projeto — bug reportado em SIMULADOR3.
    // Phase 12.60 — `etapasLocais` / `negociosLocais` já foram capturados
    // acima (pelo `projeto_id` local, ANTES do dispatch de RECONCILE).
    const etapaIdMap = new Map(); // localId → backendId
    for (const e of etapasLocais) {
      try {
        const criada = await sprints360Api.etapas.criar({
          projeto_id: idBackend,
          versao_id:  resolveVersao(e.versao_id),
          ordem: e.ordem || 1, nome: e.nome || "Etapa", cor: e.cor || null,
        });
        etapaIdMap.set(e.id, criada.id);
        dispatch({ type: "RECONCILE_BACKEND_ID", payload: {
          tipo: "etapa", id_local: e.id, id_backend: criada.id,
        }});
      } catch (err) {
        console.warn("[promote etapa]", e.nome, err?.message || err);
      }
    }

    const negocioIdMap = new Map();
    for (const n of negociosLocais) {
      try {
        const criado = await sprints360Api.negocios.criar({
          projeto_id: idBackend,
          versao_id:  resolveVersao(n.versao_id),
          nome:       n.nome || "Negócio",
          origem:     n.origem || "manual",
        });
        negocioIdMap.set(n.id, criado.id);
        dispatch({ type: "RECONCILE_BACKEND_ID", payload: {
          tipo: "negocio", id_local: n.id, id_backend: criado.id,
        }});
      } catch (err) {
        console.warn("[promote negocio]", n.nome, err?.message || err);
      }
    }

    // 2.2) (d) SPRINTS locais — mesmo problema das versões: nunca eram criados.
    const sprintIdMap = new Map();
    const sprintsLocais = stateRef.current.sprints.filter(
      sp => sp.projeto_id === projetoId && String(sp.id).startsWith("spr-")
    );
    for (const sp of sprintsLocais) {
      try {
        const criado = await sprints360Api.sprints.criar({
          projeto_id: idBackend,
          versao_id: resolveVersao(sp.versao_id),
          numero: sp.numero || 1,
          nome: sp.nome || `Sprint ${sp.numero || 1}`,
          descricao: sp.descricao || null,
          data_inicio: sp.data_inicio || null,
          data_fim: sp.data_fim || null,
        });
        sprintIdMap.set(sp.id, criado.id);
        dispatch({ type: "RECONCILE_BACKEND_ID", payload: {
          tipo: "sprint", id_local: sp.id, id_backend: criado.id,
        }});
      } catch (err) {
        console.warn("[promote sprint]", sp.nome, err?.message || err);
      }
    }

    // 3) Cria cada atividade filha no backend (dedup por título)
    let atividadesBackendExistentes = [];
    try {
      const r = await sprints360Api.atividades.listar(idBackend);
      atividadesBackendExistentes = r?.atividades || [];
    } catch { /* segue sem dedup — pior caso duplica atividade */ }

    // Resolve FK helpers (converte id local → id backend quando existir no mapa)
    const resolveEtapa   = (id) => (id ? (etapaIdMap.get(id)   || (String(id).startsWith("etp-") ? null : id)) : null);
    const resolveNegocio = (id) => (id ? (negocioIdMap.get(id) || (String(id).startsWith("neg-") ? null : id)) : null);
    const resolveSprint  = (id) => (id ? (sprintIdMap.get(id)  || (String(id).startsWith("spr-") ? null : id)) : null);

    let promovidas = 0;
    let falhas = 0;
    let ja_existiam = 0;
    const atividadeIdMap = new Map(); // Fev/2026 — local 'ati-' -> backend UUID (usado abaixo p/ dependências)
    for (const a of filhasLocais) {
      // Skip se atividade já é backend
      if (a._origem === "backend" && !String(a.id).startsWith("ati-")) continue;
      // Skip se já existe atividade com mesmo título no projeto backend
      if (atividadesBackendExistentes.some(x => x.titulo === a.titulo)) {
        ja_existiam++;
        continue;
      }
      try {
        const c = await sprints360Api.atividades.criar({
          projeto_id: idBackend,
          titulo: a.titulo || "(sem título)",
          descricao: a.descricao || null,
          status: mapStatusUiToBackend(a.status || "a_iniciar"),
          etapa_id:   resolveEtapa(a.etapa_id),
          negocio_id: resolveNegocio(a.negocio_id),
          sprint_id:  resolveSprint(a.sprint_id),
          versao_id:  resolveVersao(a.versao_id),
          sprint_num: a.sprint_num ?? null,
          // Mesmo fallback canônico do write-through: a UI usa
          // `data_inicio`/`data_fim`; o backend só tem as colunas "_prevista".
          // Sem isso o promote subia as atividades SEM DATA e o Cronograma
          // exibia "Nenhuma atividade nesta versão" (o `range` do Gantt exige
          // início E fim em alguma atividade).
          data_inicio_prevista: a.data_inicio_prevista || a.data_inicio || null,
          data_inicio_real: a.data_inicio_real || null,
          data_fim_prevista: a.data_fim_prevista || a.data_fim || null,
          data_fim_real: a.data_fim_real || null,
          horas_est: a.horas_estimadas ?? null,
          horas_real: a.horas_realizadas ?? null,
          percentual_concluido: a.percentual_concluido ?? 0,
          responsavel_nome: a.responsavel_nome || null,
          nivel: a.nivel || null,
        });
        atividadeIdMap.set(a.id, c.id);
        dispatch({ type: "RECONCILE_BACKEND_ID", payload: {
          tipo: "atividade", id_local: a.id, id_backend: c.id,
        }});
        promovidas++;
      } catch (e) {
        falhas++;
        console.warn("[promote atividade]", a.titulo, e?.message || e);
      }
    }
    console.log(`[promote] "${cur.nome}" concluído · projeto UUID=${idBackend} · ${versoesLocais.length} versões · ${sprintsLocais.length} sprints · ${promovidas}/${filhasLocais.length} atividades OK · ${ja_existiam} já existiam · ${falhas} falhas`);

    // Fev/2026 — persiste as dependências (`depende_de`) agora que toda
    // atividade promovida nesta leva tem um UUID backend. Antes, o promote
    // só criava as atividades: a dependência aparecia na tela (o reducer já
    // reescreve `depende_de` local→backend via RECONCILE_BACKEND_ID), mas
    // nunca virava uma linha em `sprints.dependencias` — e sumia no
    // próximo reload/hidratação.
    const resolveAtividadeDep = (id) => atividadeIdMap.get(id) || (String(id).startsWith("ati-") ? null : id);
    for (const a of filhasLocais) {
      const deps = Array.isArray(a.depende_de) ? a.depende_de : [];
      const destinoId = resolveAtividadeDep(a.id);
      if (!destinoId || deps.length === 0) continue;
      for (const predIdLocal of deps) {
        const origemId = resolveAtividadeDep(predIdLocal);
        if (!origemId) continue;
        try {
          await sprints360Api.dependencias.criar({
            projeto_id: idBackend, atividade_origem_id: origemId,
            atividade_destino_id: destinoId, tipo: "fim_para_inicio",
          });
        } catch (err) {
          if (err?.response?.status !== 409) console.warn("[promote dependencia]", a.titulo, err?.message || err);
        }
      }
    }

    // (b) Com os ids reconciliados, esvazia a fila do que ficou pendente
    // enquanto o projeto ainda era local.
    setTimeout(() => drainPendingSync(), 0);

    return idBackend;
    } finally {
      promoteInFlightRef.current.delete(projetoId);
    }
  }, [isRealSession, drainPendingSync]);

  // Phase 12.34 — mantém a ref sempre apontando para a versão atual (usada
  // por createProjeto no fire-and-forget do auto-promote).
  useEffect(() => { promoteProjetoParaBackendRef.current = promoteProjetoParaBackend; }, [promoteProjetoParaBackend]);

  // Phase 12.9 — Backfill em LOTE: varre todos os projetos locais do state
  // e promove cada um para o backend. Idempotente (skipa os já backend).
  // Retorna { total, promovidos, ja_backend, falhas: [{id, nome, erro}] }.
  //
  // Uso: chamado automaticamente ao entrar em "Ações e Decisões" para
  // garantir que o motor MGC tenha visibilidade de TODOS os projetos do
  // tenant, não só do ativo.
  const promoteAllLocalProjects = useCallback(async (opts = {}) => {
    // Phase 12.28r — Opção `apenasId` restringe a promoção a um único
    // projeto (usado por telas que operam sobre o projeto ativo).
    const rel = { total: 0, promovidos: 0, ja_backend: 0, falhas: [] };
    if (!isRealSession()) return rel;
    const locais = stateRef.current.projetos.filter(p => {
      const eLocal = String(p.id).startsWith("prj-") || p._origem !== "backend";
      if (!eLocal) return false;
      return opts.apenasId ? p.id === opts.apenasId : true;
    });
    const backends = opts.apenasId
      ? []
      : stateRef.current.projetos.filter(p => !String(p.id).startsWith("prj-") && p._origem === "backend");
    rel.total = locais.length + backends.length;
    rel.ja_backend = backends.length;
    // Sequencial (evita explodir o backend com N POST simultâneos)
    for (const p of locais) {
      try {
        const novo = await promoteProjetoParaBackend(p.id);
        if (novo && novo !== p.id) rel.promovidos++;
      } catch (e) {
        rel.falhas.push({ id: p.id, nome: p.nome, erro: e?.message || String(e) });
      }
    }
    return rel;
  }, [isRealSession, promoteProjetoParaBackend]);

  // ---------------------------------------------------------------
  // Phase 12.9 — Write-through Atividades + Projetos (P0)
  // ---------------------------------------------------------------
  // `dispatchSync` faz o dispatch normal (otimista, imediato) e — quando
  // há sessão real e a ação é reconhecida — dispara fire-and-forget o
  // POST/PATCH/DELETE correspondente no backend. Após criação, reconcilia
  // o id local (`ati-...`, `prj-...`) pelo UUID retornado pelo servidor
  // (via ação `RECONCILE_BACKEND_ID`, que também religa todos os filhos
  // `projeto_id === id_local` para o novo UUID).
  //
  // Regras:
  //   • Se o payload já vier com `_origem === "backend"` → não recria
  //     (é o próprio bootstrap injetando itens vindos do /GET).
  //   • Erros são registrados em console mas NÃO revertem o state local.
  //     Isso mantém a UX fluida; um follow-up pode adicionar "toast +
  //     replay" se o offline persistir.
  //   • Bucket `__mock` (usuário DEMO) nunca sincroniza.
  const dispatchSync = useCallback((action) => {
    // 1) Dispatch otimista imediato
    dispatch(action);
    if (!isRealSession()) return;

    // Phase 12.10 — Detecção de mudança: qualquer mutação relevante em
    // atividade/projeto marca o projeto correspondente como "dirty" no
    // s360MotorState. A tela Ações & Decisões usa isso para decidir se
    // dispara motor+Val automaticamente ao entrar (gatilho por mudança,
    // não por tempo).
    try {
      switch (action.type) {
        case "ATIVIDADE_ADD":
        case "ATIVIDADE_UPDATE": {
          const p = action.payload || {};
          // Para ADD, projeto_id já vem no payload.
          // Para UPDATE, se não vier, resolvemos pela atividade no state.
          let projetoId = p.projeto_id;
          if (!projetoId && p.id) {
            const a = stateRef.current.atividades.find(x => x.id === p.id);
            projetoId = a?.projeto_id || null;
          }
          if (projetoId) markProjetoDirty(projetoId);
          break;
        }
        case "ATIVIDADE_DEL": {
          const a = stateRef.current.atividades.find(x => x.id === action.id);
          if (a?.projeto_id) markProjetoDirty(a.projeto_id);
          break;
        }
        case "PROJETO_ADD":
        case "PROJETO_UPDATE": {
          const p = action.payload || {};
          if (p.id) markProjetoDirty(p.id);
          break;
        }
        // Phase 12.20 — mudanças em sprints/etapas/negocios/versoes também
        // afetam a leitura da Val (governança, cadência, escopo). Marca o
        // projeto correspondente como dirty.
        case "SPRINT_ADD":
        case "SPRINT_UPDATE":
        case "ETAPA_ADD":
        case "ETAPA_UPDATE":
        case "NEGOCIO_ADD":
        case "NEGOCIO_UPDATE":
        case "VERSAO_ADD":
        case "VERSAO_UPDATE": {
          const p = action.payload || {};
          let projetoId = p.projeto_id;
          if (!projetoId && p.id) {
            // UPDATE sem projeto_id → resolve pela coleção do state
            const col = (
              action.type.startsWith("SPRINT")  ? stateRef.current.sprints  :
              action.type.startsWith("ETAPA")   ? stateRef.current.etapas   :
              action.type.startsWith("NEGOCIO") ? stateRef.current.negocios :
              stateRef.current.versoes
            );
            const it = col.find(x => x.id === p.id);
            projetoId = it?.projeto_id || null;
          }
          if (projetoId) markProjetoDirty(projetoId);
          break;
        }
        case "SPRINT_DEL":
        case "ETAPA_DEL":
        case "NEGOCIO_DEL":
        case "VERSAO_DEL": {
          const col = (
            action.type.startsWith("SPRINT")  ? stateRef.current.sprints  :
            action.type.startsWith("ETAPA")   ? stateRef.current.etapas   :
            action.type.startsWith("NEGOCIO") ? stateRef.current.negocios :
            stateRef.current.versoes
          );
          const it = col.find(x => x.id === action.id);
          if (it?.projeto_id) markProjetoDirty(it.projeto_id);
          break;
        }
        case "VERSOES_BULK": {
          // Update em lote — marca dirty para cada projeto tocado.
          const projs = new Set();
          for (const v of (action.payload || [])) {
            if (v.projeto_id) projs.add(v.projeto_id);
          }
          projs.forEach(pid => markProjetoDirty(pid));
          break;
        }
        default: break;
      }
    } catch { /* ignore */ }

    // 2) Fire-and-forget backend
    syncToBackendRef.current?.(action);
  }, [isRealSession]);

  // ---------------------------------------------------------------
  // Write-through para o backend (fire-and-forget).
  // Extraído de `dispatchSync` em Jun/2026 para que a FILA DE SINCRONIZAÇÃO
  // possa reenviar a mesma ação depois que os ids locais forem reconciliados.
  // ---------------------------------------------------------------
  const syncToBackend = useCallback((action, opts = {}) => {
    const daFila = !!opts.daFila;
    void daFila;
    if (!isRealSession()) return;
    try {
      switch (action.type) {
        case "ATIVIDADE_ADD": {
          const a = action.payload || {};
          if (a._origem === "backend") return;
          // projeto_id precisa ser UUID válido (backend valida) — se o
          // projeto ainda é local (id `prj-…`), pulamos o sync até o
          // projeto ter sido reconciliado.
          if (!a.projeto_id || String(a.projeto_id).startsWith("prj-")) return enqueueSync(action, "projeto ainda local");
          const payload = {
            projeto_id: a.projeto_id,
            versao_id:  a.versao_id  && !String(a.versao_id).startsWith("ver-")  ? a.versao_id  : null,
            titulo: a.titulo || "(sem título)",
            descricao: a.descricao || null,
            status: mapStatusUiToBackend(a.status),
            sprint_num: a.sprint_num ?? null,
            // Phase 12.32 — FKs persistidas em sprints.atividades. Só envia
            // se já forem UUIDs (id local `spr-`/`etp-`/`neg-` seria rejeitado
            // pela validação Pydantic).
            sprint_id:  a.sprint_id  && !String(a.sprint_id).startsWith("spr-")   ? a.sprint_id  : null,
            etapa_id:   a.etapa_id   && !String(a.etapa_id).startsWith("etp-")    ? a.etapa_id   : null,
            negocio_id: a.negocio_id && !String(a.negocio_id).startsWith("neg-")  ? a.negocio_id : null,
            // Jun/2026 — FALLBACK CANÔNICO. O backend só tem as colunas
            // "_prevista"; a UI trabalha com `data_inicio`/`data_fim`
            // canônicos (importação do V360, grade manual, Simulador). Sem o
            // fallback, atividades importadas iam ao banco SEM DATA e o
            // Cronograma renderizava "Nenhuma atividade nesta versão" (o
            // `range` do Gantt exige início E fim).
            data_inicio_prevista: a.data_inicio_prevista || a.data_inicio || null,
            data_inicio_real: a.data_inicio_real || null,
            data_fim_prevista: a.data_fim_prevista || a.data_fim || null,
            data_fim_real: a.data_fim_real || null,
            horas_est: a.horas_estimadas ?? null,
            horas_real: a.horas_realizadas ?? null,
            percentual_concluido: a.percentual_concluido ?? 0,
            responsavel_nome: a.responsavel_nome || null,
            // Fev/2026 — nível do profissional (Pleno/Sênior/Júnior), usado
            // pela exportação V360 e pelo Simulador de Cronograma.
            nivel: a.nivel || null,
          };
          sprints360Api.atividades.criar(payload)
            .then(created => {
              dispatch({ type: "RECONCILE_BACKEND_ID", payload: {
                tipo: "atividade", id_local: a.id, id_backend: created.id,
                // Out/2026 — a posição "#" definitiva é atribuída pelo
                // backend no momento da criação (sempre ao final do grupo
                // projeto+versão) — adota o valor confirmado em vez do
                // numero otimista calculado localmente, evitando drift.
                patch: { numero: created.ordem_sequencial, updated_at: created.updated_at },
              }});
              // Bugfix (Ago/2026): persiste predecessoras já selecionadas no
              // momento da CRIAÇÃO. Antes, só o ATIVIDADE_UPDATE criava a
              // dependência no backend — uma atividade nova já nascendo com
              // `depende_de` preenchido nunca gerava a linha em `dependencias`,
              // e o vínculo sumia no próximo hydrate (só existia no front).
              const deps = Array.isArray(a.depende_de) ? a.depende_de : [];
              for (const predId of deps) {
                if (!predId || String(predId).startsWith("ati-")) continue; // predecessora ainda local — resolvida no RECONCILE dela (ver abaixo)
                sprints360Api.dependencias.criar({
                  projeto_id: a.projeto_id,
                  atividade_origem_id: predId,
                  atividade_destino_id: created.id,
                  tipo: "fim_para_inicio",
                }).catch(err => { if (err?.response?.status !== 409) warnWriteThroughError("DEP_ADD", err); });
              }
            })
            .catch(err => warnWriteThroughError("ATIVIDADE_ADD", err));
          break;
        }
        case "ATIVIDADE_UPDATE": {
          const p = action.payload || {};
          if (!p.id) return;
          if (String(p.id).startsWith("ati-")) return enqueueSync(action, "atividade ainda local");
          const patch = {
            titulo: p.titulo, descricao: p.descricao,
            status: p.status ? mapStatusUiToBackend(p.status) : undefined,
            sprint_num: p.sprint_num,
            // Phase 12.32 — FKs organizacionais (idem write-through do ADD)
            sprint_id:  p.sprint_id  === null ? null : (p.sprint_id  && !String(p.sprint_id).startsWith("spr-")   ? p.sprint_id  : undefined),
            etapa_id:   p.etapa_id   === null ? null : (p.etapa_id   && !String(p.etapa_id).startsWith("etp-")    ? p.etapa_id   : undefined),
            negocio_id: p.negocio_id === null ? null : (p.negocio_id && !String(p.negocio_id).startsWith("neg-")  ? p.negocio_id : undefined),
            versao_id:  p.versao_id  === null ? null : (p.versao_id  && !String(p.versao_id).startsWith("ver-")   ? p.versao_id  : undefined),
            // Fallback canônico (ver nota no ATIVIDADE_ADD).
            data_inicio_prevista: p.data_inicio_prevista ?? p.data_inicio,
            data_inicio_real: p.data_inicio_real,
            data_fim_prevista: p.data_fim_prevista ?? p.data_fim,
            data_fim_real: p.data_fim_real,
            horas_est: p.horas_estimadas, horas_real: p.horas_realizadas,
            percentual_concluido: p.percentual_concluido,
            responsavel_nome: p.responsavel_nome,
            nivel: p.nivel,
          };
          // Remove undefined (só envia o que mudou)
          Object.keys(patch).forEach(k => patch[k] === undefined && delete patch[k]);
          // Phase 12.80 — Promessas de escrita desta ação (PATCH principal +
          // dependências criadas/excluídas). Usadas abaixo para saber QUANDO
          // é seguro buscar de volta o snapshot real do backend.
          const promessasEscrita = [];
          if (Object.keys(patch).length > 0) {
            promessasEscrita.push(
              sprints360Api.atividades.atualizar(p.id, patch)
                .catch(err => warnWriteThroughError("ATIVIDADE_UPDATE", err))
            );
          }
          // Phase 12.56 — Persiste dependências (`depende_de`) via endpoint
          // separado. Só envia pares cujos ids são ambos UUIDs backend.
          // Phase 12.62 — Alinha o payload ao contrato do backend
          // (`projeto_id`, `atividade_origem_id`, `atividade_destino_id`).
          // Antes disso, o POST enviava `atividade_id` / `depende_de_atividade_id`
          // (chaves inválidas) e o Pydantic rejeitava com 422 silenciosamente
          // (o catch de 409 não pegava). Resultado: nenhuma dependência era
          // persistida no backend, e ao re-hidratar todas as arestas sumiam.
          //
          // Phase 12.80 — Bugfix: predecessoras REMOVIDAS pelo usuário nunca
          // eram excluídas no backend. O código só percorria `p.depende_de`
          // (lista NOVA) chamando `.criar()` — nada aqui olhava para a lista
          // ANTIGA para descobrir quem saiu e chamar `.excluir()`. Resultado:
          // o vínculo sobrevivia no banco e voltava a aparecer após F5, mesmo
          // já tendo sido "salvo" sem a predecessora na tela. Agora comparamos
          // a lista antiga (snapshot de `stateRef.current`, ainda não
          // sobrescrito pelo dispatch otimista desta mesma ação — ver
          // `useEffect` que sincroniza `stateRef` só após o commit do render)
          // contra a nova para decidir o que criar e o que excluir.
          let projetoBackendId = null;
          if ("depende_de" in p) {
            const dependenteId = p.id;
            if (!String(dependenteId).startsWith("ati-")) {
              const atividadeAntes = stateRef.current.atividades.find(x => x.id === dependenteId);
              projetoBackendId = atividadeAntes?.projeto_id;
              if (projetoBackendId && !String(projetoBackendId).startsWith("prj-")) {
                const depsAntes = Array.isArray(atividadeAntes?.depende_de) ? atividadeAntes.depende_de : [];
                const depsNovas = Array.isArray(p.depende_de) ? p.depende_de : [];

                for (const predId of depsNovas) {
                  if (!predId) continue;
                  if (String(predId).startsWith("ati-")) continue;
                  if (depsAntes.includes(predId)) continue; // já existia, não recria
                  promessasEscrita.push(
                    sprints360Api.dependencias.criar({
                      projeto_id: projetoBackendId,
                      atividade_origem_id: predId,       // predecessor
                      atividade_destino_id: dependenteId, // dependente (atual)
                      tipo: "fim_para_inicio",
                    }).catch(err => {
                      const st = err?.response?.status;
                      if (st !== 409) warnWriteThroughError("DEP_ADD", err);
                    })
                  );
                }

                const predIdsRemovidos = depsAntes.filter(predId => predId && !depsNovas.includes(predId));
                if (predIdsRemovidos.length > 0) {
                  promessasEscrita.push(
                    sprints360Api.dependencias.listar(projetoBackendId)
                      .then(({ dependencias }) => {
                        const alvos = (dependencias || []).filter(d =>
                          d.atividade_destino_id === dependenteId && predIdsRemovidos.includes(d.atividade_origem_id)
                        );
                        return Promise.all(alvos.map(d =>
                          sprints360Api.dependencias.excluir(d.id)
                            .catch(err => warnWriteThroughError("DEP_DEL", err))
                        ));
                      })
                      .catch(err => warnWriteThroughError("DEP_DEL_LIST", err))
                  );
                }
              }
            }
          }
          // Phase 12.80 — Refresh pós-save: assim que TODA a escrita desta
          // ação assentar no backend (PATCH principal + criações/exclusões
          // de dependência), busca de volta o snapshot real do projeto e
          // funde no state. Fecha a janela em que uma hidratação periódica
          // concorrente, lendo o backend ANTES da exclusão comitar, poderia
          // reintroduzir uma predecessora já removida (o merge da hidratação
          // é por UNION, nunca "esquece" um item — ver `__MERGE_HYDRATED__`).
          // Também garante que a lista reflita o estado real pós-salvamento
          // sem depender do usuário recarregar a página manualmente.
          if (promessasEscrita.length > 0) {
            const projIdParaRefresh = projetoBackendId
              || stateRef.current.atividades.find(x => x.id === p.id)?.projeto_id;
            if (projIdParaRefresh && !String(projIdParaRefresh).startsWith("prj-")) {
              Promise.allSettled(promessasEscrita).then(async () => {
                try {
                  const atividadesFrescas = await fetchAtividadesDoProjetoComDeps(projIdParaRefresh);
                  dispatch({ type: "__MERGE_HYDRATED__", payload: { atividades: atividadesFrescas } });
                } catch (err) {
                  // Silencioso — a próxima hidratação periódica corrige.
                  console.warn("[s360] refresh pós-save falhou:", err?.message || err);
                }
              });
            }
          }
          break;
        }
        case "ATIVIDADE_DEL": {
          const id = action.id;
          if (!id) return;
          if (String(id).startsWith("ati-")) return enqueueSync(action, "atividade ainda local");
          sprints360Api.atividades.excluir(id)
            .catch(err => warnWriteThroughError("ATIVIDADE_DEL", err));
          break;
        }
        case "RECONCILE_BACKEND_ID": {
          // Phase 12.56 — Após reconciliar uma ETAPA ou NEGÓCIO local → UUID
          // backend, força um PATCH nas atividades filhas para persistir a FK
          // no backend (o dispatch RECONCILE já atualizou o state local, mas o
          // ATIVIDADE_UPDATE anterior tinha pulado `etapa_id` porque o id era
          // ainda local `etp-…`).
          const { tipo, id_backend } = action.payload || {};
          if (!id_backend) break;
          if (tipo === "etapa") {
            const filhas = stateRef.current.atividades.filter(x =>
              x.etapa_id === id_backend && !String(x.id).startsWith("ati-")
            );
            filhas.forEach(a => {
              sprints360Api.atividades.atualizar(a.id, { etapa_id: id_backend })
                .catch(err => warnWriteThroughError("ATIVIDADE etapa_id sync", err));
            });
          } else if (tipo === "negocio") {
            const filhas = stateRef.current.atividades.filter(x =>
              x.negocio_id === id_backend && !String(x.id).startsWith("ati-")
            );
            filhas.forEach(a => {
              sprints360Api.atividades.atualizar(a.id, { negocio_id: id_backend })
                .catch(err => warnWriteThroughError("ATIVIDADE negocio_id sync", err));
            });
          } else if (tipo === "atividade") {
            // Bugfix (Ago/2026): esta atividade (ex-local) virou backend
            // (`id_backend`). O reducer já reescreveu `depende_de` de quem a
            // referenciava como predecessora (id local → id_backend). Agora
            // que AMBOS os lados são UUIDs backend, persiste a dependência
            // que antes foi pulada (o predId ainda era "ati-…" no momento do
            // save da sucessora).
            const sucessoras = stateRef.current.atividades.filter(x =>
              !String(x.id).startsWith("ati-") &&
              Array.isArray(x.depende_de) && x.depende_de.includes(id_backend)
            );
            sucessoras.forEach(suc => {
              sprints360Api.dependencias.criar({
                projeto_id: suc.projeto_id,
                atividade_origem_id: id_backend,
                atividade_destino_id: suc.id,
                tipo: "fim_para_inicio",
              }).catch(err => { if (err?.response?.status !== 409) warnWriteThroughError("DEP_ADD", err); });
            });
          }
          break;
        }
        case "PROJETO_ADD": {
          const p = action.payload || {};
          if (p._origem === "backend") return;
          // Jun/2026 — (a) CRIADOR ÚNICO. `createProjeto` marca o payload com
          // `_promoteWillCreate` e delega a criação ao promote, que também sobe
          // versões/sprints/etapas/negócios/atividades e tem recuperação de
          // 409. Antes, os dois caminhos criavam em paralelo com `codigo`
          // diferente e `nome` igual → violação do UNIQUE de nome, 409, e o
          // dedup por nome reconciliava projetos distintos no mesmo UUID.
          if (p._promoteWillCreate) return;
          const payload = {
            codigo: p.codigo || `PRJ-${Date.now().toString(36).slice(-6).toUpperCase()}`,
            nome: p.nome || "Projeto sem nome",
            tipo_projeto: p.tipo_projeto || null,
            status: p.status === "ativo" || p.status === "planejamento" ? p.status : "ativo",
            cor: p.cor || null,
            sponsor_nome: p.sponsor_nome || null,
            gp_nome: p.gp_nome || null,
            data_inicio: p.data_inicio_prevista || null,
            data_fim_prevista: p.data_fim_prevista || null,
            cadencia_dias: p.cadencia_dias || 14,
            contexto_livre: p.contexto_livre || null,
            empresa_simulada_id: p.empresa_simulada_id || null,  // Phase 12.27
          };
          sprints360Api.projetos.criar(payload)
            .then(created => {
              dispatch({ type: "RECONCILE_BACKEND_ID", payload: {
                tipo: "projeto", id_local: p.id, id_backend: created.id,
              }});
            })
            .catch(err => warnWriteThroughError("PROJETO_ADD", err));
          break;
        }
        case "PROJETO_UPDATE": {
          const p = action.payload || {};
          if (!p.id) return;
          if (String(p.id).startsWith("prj-")) return enqueueSync(action, "projeto ainda local");
          const patch = {
            nome: p.nome, tipo_projeto: p.tipo_projeto, status: p.status, cor: p.cor,
            sponsor_nome: p.sponsor_nome, gp_nome: p.gp_nome,
            data_inicio: p.data_inicio_prevista, data_fim_prevista: p.data_fim_prevista,
            cadencia_dias: p.cadencia_dias, contexto_livre: p.contexto_livre,
          };
          Object.keys(patch).forEach(k => patch[k] === undefined && delete patch[k]);
          if (Object.keys(patch).length === 0) return;
          sprints360Api.projetos.atualizar(p.id, patch)
            .catch(err => warnWriteThroughError("PROJETO_UPDATE", err));
          break;
        }
        case "PROJETO_DEL": {
          const id = action.id;
          if (!id || String(id).startsWith("prj-")) return;
          sprints360Api.projetos.excluir(id)
            .catch(err => warnWriteThroughError("PROJETO_DEL", err));
          break;
        }
        // Phase 12.20 — Write-through de VERSOES / SPRINTS / ETAPAS / NEGOCIOS.
        // Padrão idêntico às atividades: ADD (POST + reconcile), UPDATE (PATCH),
        // DEL (DELETE). Sync silencioso via fire-and-forget.
        case "VERSAO_ADD": {
          const v = action.payload || {};
          if (v._origem === "backend") return;
          if (!v.projeto_id) return;
          if (String(v.projeto_id).startsWith("prj-")) return enqueueSync(action, "projeto ainda local");
          sprints360Api.versoes.criar({
            projeto_id: v.projeto_id,
            numero: v.numero,
            rotulo: v.rotulo,
            descricao: v.descricao || null,
            status: v.status || "rascunho",
          })
            .then(c => dispatch({ type: "RECONCILE_BACKEND_ID", payload: {
              tipo: "versao", id_local: v.id, id_backend: c.id,
            }}))
            .catch(err => warnWriteThroughError("VERSAO_ADD", err));
          break;
        }
        case "VERSAO_UPDATE": {
          const v = action.payload || {};
          if (!v.id) return;
          if (String(v.id).startsWith("ver-")) return enqueueSync(action, "versão ainda local");
          const patch = {
            rotulo: v.rotulo, rotulo_original: v.rotulo_original,
            descricao: v.descricao, status: v.status, arquivada: v.arquivada,
          };
          Object.keys(patch).forEach(k => patch[k] === undefined && delete patch[k]);
          if (Object.keys(patch).length === 0) return;
          sprints360Api.versoes.atualizar(v.id, patch)
            .catch(err => warnWriteThroughError("VERSAO_UPDATE", err));
          break;
        }
        case "VERSAO_DEL": {
          const id = action.id;
          if (!id || String(id).startsWith("ver-")) return;
          sprints360Api.versoes.excluir(id)
            .catch(err => warnWriteThroughError("VERSAO_DEL", err));
          break;
        }
        case "VERSOES_BULK": {
          // Update em lote (usado ao finalizar versão → arquiva outras).
          // Faz PATCH individual para cada versão que está no backend.
          const nova = action.payload || [];
          for (const v of nova) {
            if (!v.id || String(v.id).startsWith("ver-")) continue;
            const patch = {
              status: v.status, arquivada: v.arquivada,
              rotulo: v.rotulo, rotulo_original: v.rotulo_original,
            };
            Object.keys(patch).forEach(k => patch[k] === undefined && delete patch[k]);
            if (Object.keys(patch).length === 0) continue;
            sprints360Api.versoes.atualizar(v.id, patch)
              .catch(err => warnWriteThroughError("VERSOES_BULK item", err));
          }
          break;
        }
        case "SPRINT_ADD": {
          const s = action.payload || {};
          if (s._origem === "backend") return;
          if (!s.projeto_id) return;
          if (String(s.projeto_id).startsWith("prj-")) return enqueueSync(action, "projeto ainda local");
          const versao_id = s.versao_id && !String(s.versao_id).startsWith("ver-") ? s.versao_id : null;
          sprints360Api.sprints.criar({
            projeto_id: s.projeto_id,
            versao_id,
            numero: s.numero || 1,
            nome: s.nome || `Sprint ${s.numero || 1}`,
            descricao: s.descricao || null,
            data_inicio: s.data_inicio || null,
            data_fim: s.data_fim || null,
          })
            .then(c => dispatch({ type: "RECONCILE_BACKEND_ID", payload: {
              tipo: "sprint", id_local: s.id, id_backend: c.id,
            }}))
            .catch(err => warnWriteThroughError("SPRINT_ADD", err));
          break;
        }
        case "SPRINT_UPDATE": {
          const s = action.payload || {};
          if (!s.id) return;
          if (String(s.id).startsWith("spr-")) return enqueueSync(action, "sprint ainda local");
          const patch = {
            nome: s.nome, descricao: s.descricao,
            numero: s.numero,
            data_inicio: s.data_inicio, data_fim: s.data_fim,
            ativo: s.ativo,
          };
          Object.keys(patch).forEach(k => patch[k] === undefined && delete patch[k]);
          if (Object.keys(patch).length === 0) return;
          sprints360Api.sprints.atualizar(s.id, patch)
            .catch(err => warnWriteThroughError("SPRINT_UPDATE", err));
          break;
        }
        case "SPRINT_DEL": {
          const id = action.id;
          if (!id || String(id).startsWith("spr-")) return;
          sprints360Api.sprints.excluir(id)
            .catch(err => warnWriteThroughError("SPRINT_DEL", err));
          break;
        }
        case "ETAPA_ADD": {
          const e = action.payload || {};
          if (e._origem === "backend") return;
          if (!e.projeto_id) return;
          if (String(e.projeto_id).startsWith("prj-")) return enqueueSync(action, "projeto ainda local");
          const versao_id = e.versao_id && !String(e.versao_id).startsWith("ver-") ? e.versao_id : null;
          sprints360Api.etapas.criar({
            projeto_id: e.projeto_id,
            versao_id,
            ordem: e.ordem || 1,
            nome: e.nome || "Etapa",
            cor: e.cor || null,
          })
            .then(c => dispatch({ type: "RECONCILE_BACKEND_ID", payload: {
              tipo: "etapa", id_local: e.id, id_backend: c.id,
            }}))
            .catch(err => warnWriteThroughError("ETAPA_ADD", err));
          break;
        }
        case "ETAPA_UPDATE": {
          const e = action.payload || {};
          if (!e.id) return;
          if (String(e.id).startsWith("etp-")) return enqueueSync(action, "etapa ainda local");
          const patch = { nome: e.nome, ordem: e.ordem, cor: e.cor };
          Object.keys(patch).forEach(k => patch[k] === undefined && delete patch[k]);
          if (Object.keys(patch).length === 0) return;
          sprints360Api.etapas.atualizar(e.id, patch)
            .catch(err => warnWriteThroughError("ETAPA_UPDATE", err));
          break;
        }
        case "ETAPA_DEL": {
          const id = action.id;
          if (!id || String(id).startsWith("etp-")) return;
          sprints360Api.etapas.excluir(id)
            .catch(err => warnWriteThroughError("ETAPA_DEL", err));
          break;
        }
        case "NEGOCIO_ADD": {
          const n = action.payload || {};
          if (n._origem === "backend") return;
          if (!n.projeto_id) return;
          if (String(n.projeto_id).startsWith("prj-")) return enqueueSync(action, "projeto ainda local");
          const versao_id = n.versao_id && !String(n.versao_id).startsWith("ver-") ? n.versao_id : null;
          sprints360Api.negocios.criar({
            projeto_id: n.projeto_id,
            versao_id,
            nome: n.nome || "Negócio",
            origem: n.origem || "manual",
          })
            .then(c => dispatch({ type: "RECONCILE_BACKEND_ID", payload: {
              tipo: "negocio", id_local: n.id, id_backend: c.id,
            }}))
            .catch(err => warnWriteThroughError("NEGOCIO_ADD", err));
          break;
        }
        case "NEGOCIO_UPDATE": {
          const n = action.payload || {};
          if (!n.id) return;
          if (String(n.id).startsWith("neg-")) return enqueueSync(action, "negócio ainda local");
          const patch = { nome: n.nome, origem: n.origem };
          Object.keys(patch).forEach(k => patch[k] === undefined && delete patch[k]);
          if (Object.keys(patch).length === 0) return;
          sprints360Api.negocios.atualizar(n.id, patch)
            .catch(err => warnWriteThroughError("NEGOCIO_UPDATE", err));
          break;
        }
        case "NEGOCIO_DEL": {
          const id = action.id;
          if (!id || String(id).startsWith("neg-")) return;
          sprints360Api.negocios.excluir(id)
            .catch(err => warnWriteThroughError("NEGOCIO_DEL", err));
          break;
        }
        default: break;
      }
    } catch (e) {
      console.warn("[write-through] side-effect error:", e?.message || e);
    }
  }, [isRealSession, enqueueSync]);

  useEffect(() => { syncToBackendRef.current = syncToBackend; }, [syncToBackend]);

  // ---------- helpers de leitura ----------
  // Phase 12.56 — Resolve tanto por id atual quanto por ids locais anteriores
  // preservados em `_prevIds` (populados pelo RECONCILE_BACKEND_ID). Isso mantém
  // consumidores (URL, componentes montados) transparentes durante a promoção
  // otimista → backend, evitando remounts que zeram state local (ex.: modais).
  const getProjeto = useCallback(
    (id) => state.projetos.find(p => p.id === id || (p._prevIds || []).includes(id)) || null,
    [state.projetos]
  );

  const getVersoesDoProjeto = useCallback(
    (projetoId) => state.versoes.filter(v => v.projeto_id === projetoId).sort((a, b) => a.numero - b.numero),
    [state.versoes],
  );

  const getVersaoAtiva = useCallback((projetoId) => {
    const p = state.projetos.find(pp => pp.id === projetoId);
    if (!p) return null;
    const versoes = state.versoes.filter(v => v.projeto_id === projetoId);
    if (versoes.length === 0) return null;
    return versoes.find(v => v.id === p.versao_ativa_id) || versoes[0];
  }, [state.projetos, state.versoes]);

  // Set/2026 — Fonte única de verdade para "o projeto está em rascunho?".
  // Ausência de status (versão sem `status` ou projeto legado sem
  // `status_cronograma`) SEMPRE conta como rascunho — mesmo default usado
  // por `toggleStatusCronograma` abaixo. Evita a tela mostrar "Oficializado"
  // enquanto o toggle assume "rascunho" (ou vice-versa).
  const isProjetoRascunho = useCallback((projetoId) => {
    const versaoAtiva = getVersaoAtiva(projetoId);
    if (versaoAtiva) return (versaoAtiva.status || "rascunho") === "rascunho";
    const p = state.projetos.find(pp => pp.id === projetoId);
    return (p?.status_cronograma || "rascunho") === "rascunho";
  }, [state.projetos, getVersaoAtiva]);

  /**
   * Retorna os dados do projeto. Se `versaoId` for informado, filtra por ela;
   * senão, pela versão ativa. Projetos legados sem versões: retorna tudo.
   */
  const projetoData = useCallback((projetoId, versaoId) => {
    const versoesDoProj = state.versoes.filter(v => v.projeto_id === projetoId);
    const usarVersao = versoesDoProj.length > 0;
    let vid = versaoId;
    if (usarVersao && !vid) {
      const p = state.projetos.find(pp => pp.id === projetoId);
      vid = p?.versao_ativa_id || versoesDoProj[0].id;
    }
    // Phase 12.21 · Retrocompat: itens sem versao_id (legacy, importados antes
    // da Phase 12.20) aparecem em qualquer versão ativa. Sem isso, projetos
    // que ganham `versao_ativa_id` ao rodar o hidrator escondem suas atividades
    // originais.
    const filtroVersao = (item) => !usarVersao || item.versao_id === vid || item.versao_id == null;
    return {
      sprints:    state.sprints   .filter(s => s.projeto_id === projetoId && filtroVersao(s)).sort((a, b) => (a.numero || 0) - (b.numero || 0)),
      etapas:     state.etapas    .filter(e => e.projeto_id === projetoId && filtroVersao(e)).sort((a, b) => (a.ordem  || 0) - (b.ordem  || 0)),
      negocios:   state.negocios  .filter(n => n.projeto_id === projetoId && filtroVersao(n)),
      atividades: state.atividades.filter(t => t.projeto_id === projetoId && filtroVersao(t)).sort((a, b) => (a.numero || 0) - (b.numero || 0)),
      rituais:    (state.rituais || []).filter(r => r.projeto_id === projetoId && filtroVersao(r)),
      versaoAtualId: vid || null,
    };
  }, [state]);

  // ---------- versionamento ----------
  const createVersao = useCallback((projetoId, opts = {}) => {
    const versoesExistentes = state.versoes.filter(v => v.projeto_id === projetoId);
    const proxNumero = (versoesExistentes.reduce((mx, v) => Math.max(mx, v.numero || 0), 0)) + 1;
    const novaVersao = {
      id: genId("ver"),
      projeto_id: projetoId,
      numero: proxNumero,
      rotulo: opts.rotulo || `v${proxNumero}`,
      descricao: opts.descricao || "",
      status: "rascunho",
      arquivada: false,
      created_at: new Date().toISOString(),
    };
    dispatchSync({ type: "VERSAO_ADD", payload: novaVersao });

    // Se houver versão de origem, clona atividades/sprints/etapas/negocios
    if (opts.copiarDe) {
      const src = { versao_id: opts.copiarDe };
      const spr = state.sprints  .filter(s => s.projeto_id === projetoId && s.versao_id === src.versao_id)
        .map(s => ({ ...s, id: genId("spr"), versao_id: novaVersao.id }));
      const etp = state.etapas   .filter(e => e.projeto_id === projetoId && e.versao_id === src.versao_id)
        .map(e => ({ ...e, id: genId("etp"), versao_id: novaVersao.id }));
      const neg = state.negocios .filter(n => n.projeto_id === projetoId && n.versao_id === src.versao_id)
        .map(n => ({ ...n, id: genId("neg"), versao_id: novaVersao.id }));
      // Mapa etapa antiga → nova (para religar atividades)
      const etpMap = Object.fromEntries(state.etapas
        .filter(e => e.projeto_id === projetoId && e.versao_id === src.versao_id)
        .map((e, i) => [e.id, etp[i].id]));
      const ati = state.atividades.filter(t => t.projeto_id === projetoId && t.versao_id === src.versao_id)
        .map(t => ({
          ...t,
          id: genId("ati"),
          versao_id: novaVersao.id,
          etapa_id: etpMap[t.etapa_id] || t.etapa_id,
        }));
      dispatch({ type: "BULK_ADD_CLONE", payload: { sprints: spr, etapas: etp, negocios: neg, atividades: ati } });
    }
    return novaVersao;
  }, [state]);

  const setVersaoAtiva = useCallback((projetoId, versaoId) => {
    dispatchSync({ type: "PROJETO_UPDATE", payload: { id: projetoId, versao_ativa_id: versaoId } });
  }, [dispatchSync]);

  const updateVersao = useCallback((versaoId, patch) => {
    dispatchSync({ type: "VERSAO_UPDATE", payload: { id: versaoId, ...patch } });
  }, [dispatchSync]);

  /**
   * Alterna rascunho ↔ finalizado da versão informada. Ao finalizar,
   * se existir OUTRA versão já finalizada, essa outra é arquivada
   * com rótulo "Old vN — DD/MM/YYYY" (regra do usuário).
   */
  const toggleStatusVersao = useCallback((versaoId) => {
    const versao = state.versoes.find(v => v.id === versaoId);
    if (!versao) return null;
    const novo = versao.status === "finalizado" ? "rascunho" : "finalizado";
    const outrasVersoes = state.versoes.filter(v => v.projeto_id === versao.projeto_id && v.id !== versaoId);
    let novasVersoes = state.versoes.map(v => {
      if (v.id === versaoId) return { ...v, status: novo, finalized_at: novo === "finalizado" ? new Date().toISOString() : v.finalized_at };
      return v;
    });
    if (novo === "finalizado") {
      const hoje = new Date().toLocaleDateString("pt-BR");
      novasVersoes = novasVersoes.map(v => {
        if (v.projeto_id === versao.projeto_id && v.id !== versaoId && v.status === "finalizado" && !v.arquivada) {
          return {
            ...v,
            status: "finalizado",
            arquivada: true,
            rotulo_original: v.rotulo,
            rotulo: `Old ${v.rotulo} — ${hoje}`,
          };
        }
        return v;
      });
    }
    dispatchSync({ type: "VERSOES_BULK", payload: novasVersoes });
    // Atualiza status_cronograma "shortcut" no projeto (usado por Projetos.jsx)
    dispatchSync({ type: "PROJETO_UPDATE", payload: { id: versao.projeto_id, status_cronograma: novo } });
    return novo;
  }, [state.versoes]);

  // ---------- criação de projeto (+ v1 automática) ----------
  /**
   * Cria projeto, versão inicial (v1), etapas default e sprints iniciais.
   * Retorna o projeto ENRIQUECIDO com `_etapas_criadas` e `_sprints_criados`
   * (cada item com seu `id` real) — o chamador pode usar essa referência
   * imediatamente para inserir negócios/atividades no MESMO tick, sem
   * depender de re-render do state (evita bug de closure obsoleto).
   *
   * Phase 12.28r · guard consultoria (raiz):
   *   Se o workspace tem plano S360-CONSULTING e o payload não traz
   *   empresa_simulada_id, injetamos o id da empresa cliente ativa
   *   (lido do localStorage `s360_consult_ativa_id_v2`). Sem empresa
   *   ativa, lançamos erro — evita que o projeto vá pro banco com
   *   empresa_simulada_id=NULL e "desapareça" dos filtros da consultoria.
   */
  const createProjeto = useCallback((data) => {
    try {
      const rawSession = typeof window !== "undefined" ? window.localStorage.getItem("app_session") : null;
      const session = rawSession ? JSON.parse(rawSession) : null;
      const planoCodigo = String(session?.plano_codigo || "").toUpperCase();
      // Phase 12.29 — Suporta plano composto (`S360-CONSULTING + V360-...`)
      const isConsulting = planoCodigo.split("+").map(s => s.trim()).some(p => p === "S360-CONSULTING");
      if (isConsulting && !data?.empresa_simulada_id) {
        let ativaId = typeof window !== "undefined" ? window.localStorage.getItem("s360_consult_ativa_id_v2") : null;
        // Regra (Jun/2026): nunca depender de sorte. Sem empresa ativa no seletor,
        // usa a PRIMEIRA empresa da lista como padrão automaticamente (e a seleciona),
        // em vez de lançar erro e deixar o projeto órfão.
        if (!ativaId) {
          try {
            const ids = JSON.parse(window.localStorage.getItem("s360_consult_ids_v1") || "[]");
            if (Array.isArray(ids) && ids.length > 0) {
              ativaId = ids[0];
              window.localStorage.setItem("s360_consult_ativa_id_v2", ativaId);
            }
          } catch { /* noop */ }
        }
        if (ativaId) {
          data = { ...data, empresa_simulada_id: ativaId };
        } else {
          throw new Error("Nenhuma empresa cliente cadastrada. Crie uma empresa em Empresa e Projetos antes de criar o projeto.");
        }
      }
    } catch (e) {
      if (e?.message?.includes("empresa cliente")) throw e;
      // Falhas silenciosas de parse não devem quebrar o fluxo.
    }
    const projId = genId("prj");
    const versaoInicial = {
      id: genId("ver"),
      projeto_id: projId,
      numero: 1,
      rotulo: "v1",
      descricao: "",
      status: data.status_cronograma || "rascunho",
      arquivada: false,
      created_at: new Date().toISOString(),
    };
    const proj = {
      id: projId,
      created_at: new Date().toISOString(),
      status_cronograma: versaoInicial.status,
      versao_ativa_id: versaoInicial.id,
      // Jun/2026 — (a) `codigo` é gerado AQUI, uma única vez, e guardado no
      // state local. Antes cada tentativa de sincronização gerava um código
      // novo e o código do backend nunca voltava para o cliente; sem código
      // estável, a deduplicação do promote caía no `nome` — e nome repetido
      // fazia dois projetos distintos colidirem no mesmo UUID.
      codigo: data.codigo || `PRJ-${Date.now().toString(36).slice(-6).toUpperCase()}`,
      ...data,
      // garante que o override do status_cronograma no data não sobrescreva versao_ativa_id
    };
    proj.versao_ativa_id = versaoInicial.id;
    if (!proj.codigo) proj.codigo = `PRJ-${Date.now().toString(36).slice(-6).toUpperCase()}`;

    // Pré-computa etapas/sprints com IDs para retornar ao chamador
    const etapasCriadas = (data._etapas_default || []).map((e, i) => ({
      id: genId("etp"), projeto_id: proj.id, versao_id: versaoInicial.id, ordem: i + 1, ...e,
    }));
    const sprintsCriados = (data._sprints_iniciais || []).map((s) => ({
      id: genId("spr"), projeto_id: proj.id, versao_id: versaoInicial.id, ativo: true, ...s,
    }));

    // `_promoteWillCreate` impede o write-through de criar em paralelo com o
    // promote (ver case PROJETO_ADD em syncToBackend).
    dispatchSync({ type: "PROJETO_ADD", payload: { ...proj, _promoteWillCreate: true } });
    dispatchSync({ type: "VERSAO_ADD", payload: versaoInicial });
    etapasCriadas.forEach(e => dispatchSync({ type: "ETAPA_ADD", payload: e }));
    sprintsCriados.forEach(s => dispatchSync({ type: "SPRINT_ADD", payload: s }));

    // Retorna o projeto com as coleções recém-criadas para uso imediato
    proj._etapas_criadas  = etapasCriadas;
    proj._sprints_criados = sprintsCriados;

    // Phase 12.34 — Promoção automática ao backend logo após a criação.
    // Antes disso, o projeto nascia "vivo só no navegador" e o motor MGC
    // não conseguia analisá-lo até o usuário clicar "Sincronizar agora"
    // em Ações e Decisões. Agora o promote roda em background (não bloqueia
    // a UI) e o próximo entry em Ações e Decisões já encontra o projeto no
    // backend.
    //
    // Fire-and-forget intencional: erros são logados mas não interrompem
    // o fluxo de criação (o auto-run de S360AcoesDecisoes ainda tem o
    // safety-net em runBulkBackfill).
    setTimeout(() => {
      promoteProjetoParaBackendRef.current?.(projId)?.catch(err => {
        console.warn(`[createProjeto] auto-promote falhou p/ ${projId}:`, err?.message || err);
      });
    }, 0);

    return proj;
  }, []);

  // Retrocompat: toggle no projeto continua funcionando (delegando à versão ativa)
  const toggleStatusCronograma = useCallback((projetoId) => {
    const versaoAtiva = getVersaoAtiva(projetoId);
    if (!versaoAtiva) {
      // Projeto legado sem versão — usa o campo do projeto (comportamento anterior).
      const p = state.projetos.find(pp => pp.id === projetoId);
      if (!p) return null;
      const novo = (p.status_cronograma || "rascunho") === "finalizado" ? "rascunho" : "finalizado";
      dispatchSync({ type: "PROJETO_UPDATE", payload: { id: projetoId, status_cronograma: novo } });
      return novo;
    }
    return toggleStatusVersao(versaoAtiva.id);
  }, [state.projetos, getVersaoAtiva, toggleStatusVersao]);

  // ---------- atividades ----------
  const calcDataInicioComDeps = useCallback((depIds, fallback) => {
    if (!depIds || depIds.length === 0) return fallback;
    const deps = state.atividades.filter(t => depIds.includes(t.id));
    if (deps.length === 0) return fallback;
    const maxFim = deps.reduce((mx, d) => {
      const f = d.data_fim || d.data_fim_prevista || "";
      return f > mx ? f : mx;
    }, "");
    return maxFim ? proximoDiaUtil(maxFim) : fallback;
  }, [state.atividades]);

  /**
   * Aplica a regra canônica SB-06 de datas em uma atividade "efetiva"
   * (patch + atual mesclados). Retorna { data_inicio, data_fim, forced }.
   *
   * Regra (feedback do usuário 31/Jul/2026):
   *   • Se houver predecessoras com fim conhecido:
   *       data_inicio = próximo dia útil após max(pred.fim)   [D+1 útil]
   *       data_fim    = novoInicio + delta calendárico atual (preserva duração
   *                     que o usuário já havia digitado). Se não houver delta
   *                     válido (nova atividade sem fim), cai para
   *                     calcDataFim(novoInicio, horas_estimadas).
   *   • Sem predecessoras:
   *       respeita data_inicio digitado; computa data_fim apenas se ausente.
   */
  const computeDepsAdjustedDates = useCallback((effective, atualPrev = null) => {
    const depIds = effective.depende_de || [];
    const deps = depIds.map(id => state.atividades.find(t => t.id === id)).filter(Boolean);
    const maxFim = deps.reduce((mx, p) => {
      const f = p.data_fim || p.data_fim_prevista || "";
      return f > mx ? f : mx;
    }, "");
    if (maxFim) {
      const dataInicio = proximoDiaUtil(maxFim);
      // Preserva o delta calendárico entre inicio e fim que já existia
      // (do estado anterior — atualPrev — quando disponível, senão do
      //  próprio effective).
      const iniAntigo = atualPrev?.data_inicio || effective.data_inicio;
      const fimAntigo = atualPrev?.data_fim    || effective.data_fim;
      let dataFim = "";
      if (iniAntigo && fimAntigo) {
        const deltaMs = new Date(fimAntigo).getTime() - new Date(iniAntigo).getTime();
        if (deltaMs >= 0) {
          const novoFim = new Date(new Date(dataInicio).getTime() + deltaMs);
          dataFim = novoFim.toISOString().slice(0, 10);
        }
      }
      if (!dataFim) dataFim = calcDataFim(dataInicio, effective.horas_estimadas);
      return { data_inicio: dataInicio, data_fim: dataFim, forced: true };
    }
    const dataInicio = effective.data_inicio || new Date().toISOString().slice(0, 10);
    const dataFim    = effective.data_fim || calcDataFim(dataInicio, effective.horas_estimadas);
    return { data_inicio: dataInicio, data_fim: dataFim, forced: false };
  }, [state.atividades]);

  const createAtividade = useCallback((data) => {
    // Preenche versao_id com a versão ativa do projeto, se disponível
    let versaoId = data.versao_id;
    if (!versaoId && data.projeto_id) {
      const p = state.projetos.find(pp => pp.id === data.projeto_id);
      versaoId = p?.versao_ativa_id;
    }
    // Datas canônicas via regra de dependência (D+1 útil)
    const { data_inicio: dataInicioFinal, data_fim: dataFimFinal } = computeDepsAdjustedDates({
      ...data,
      data_inicio: data.data_inicio || new Date().toISOString().slice(0, 10),
    });
    const ati = {
      id: genId("ati"),
      status: "pendente",
      created_at: new Date().toISOString(),
      ...data,
      versao_id: versaoId || null,
      data_inicio: dataInicioFinal,
      data_fim: dataFimFinal,
      data_inicio_prevista: dataInicioFinal,
      data_fim_prevista:    dataFimFinal,
    };
    dispatchSync({ type: "ATIVIDADE_ADD", payload: ati });
    return ati;
  }, [computeDepsAdjustedDates, state.projetos]);

  // -------------------- SB-06 helpers --------------------

  /** Retorna atividades que dependem da atividade `id` (sucessoras). */
  const getDependentesDe = useCallback((id) => (
    state.atividades.filter(t => Array.isArray(t.depende_de) && t.depende_de.includes(id))
  ), [state.atividades]);

  /** Histórico ordenado por data desc de uma atividade específica. */
  const getAtividadeHistorico = useCallback((id) => (
    state.atividade_historico.filter(h => h.atividade_id === id)
  ), [state.atividade_historico]);

  /**
   * SB-06 update com log de alterações. Compara o objeto atual x patch e
   * registra em `atividade_historico` cada campo listado em CAMPOS_HISTORICO.
   * usuario_nome é opcional (fallback: "Sistema").
   */
  const updateAtividadeSB06 = useCallback((id, patch, opts = {}) => {
    const atual = state.atividades.find(t => t.id === id);
    if (!atual) return { ok: false, erro: "Atividade não encontrada." };

    // -------- Clampe D+1 útil da própria atividade --------
    // Só recalcula quando um gatilho REALMENTE mudou de valor. O editor mapeia
    // `data_inicio ← data_inicio_prevista` e sempre reenvia ambos; em dados
    // antigos o canônico e o espelho podem ter DIVERGIDO, então comparar
    // campo-a-campo dava "mudou" falso e reposicionava a barra a cada save.
    // Comparamos o valor SEMÂNTICO (prevista, com fallback ao canônico).
    const _norm = (v) => (v === null || v === undefined || v === "" ? null : v);
    const _start = (o) => _norm(o.data_inicio_prevista) ?? _norm(o.data_inicio);
    const _end   = (o) => _norm(o.data_fim_prevista) ?? _norm(o.data_fim);
    const _merged = { ...atual, ...patch };
    const _temInicio = ("data_inicio" in patch) || ("data_inicio_prevista" in patch);
    const _temFim    = ("data_fim" in patch) || ("data_fim_prevista" in patch);
    const _inicioMudou = _temInicio && _start(_merged) !== _start(atual);
    const _fimMudou    = _temFim    && _end(_merged)   !== _end(atual);
    const _horasMudou  = ("horas_estimadas" in patch) && _norm(patch.horas_estimadas) !== _norm(atual.horas_estimadas);
    void _horasMudou; // mantido para log/semântica, não dispara mais o clamp (ver nota abaixo)
    const _depsMudaram = ("depende_de" in patch) &&
      JSON.stringify([...(patch.depende_de || [])].sort()) !== JSON.stringify([...(atual.depende_de || [])].sort());
    // Bugfix (Ago/2026): o clamp via predecessora só deve disparar quando a
    // LISTA de dependências de fato mudou (ex.: handleDepsChange). Antes,
    // qualquer edição manual de início/fim/horas numa atividade que JÁ tinha
    // predecessoras também entrava aqui — e como o cálculo recompõe o fim
    // preservando a duração ANTIGA (ini/fim do estado anterior), a data que
    // o usuário acabou de digitar era sempre descartada e revertida ao
    // valor original. Edições diretas de data devem ser respeitadas como estão.
    const clampNeeded = _depsMudaram;
    if (clampNeeded) {
      const effective = { ...atual, ...patch };
      // Se o patch trouxer apenas o espelho "_prevista", copia para o canônico
      // para o cálculo enxergar a intenção do usuário.
      if (!("data_inicio" in patch) && "data_inicio_prevista" in patch) effective.data_inicio = patch.data_inicio_prevista;
      if (!("data_fim"    in patch) && "data_fim_prevista"    in patch) effective.data_fim    = patch.data_fim_prevista;
      const { data_inicio: novoInicio, data_fim: novoFim, forced } = computeDepsAdjustedDates(effective, atual);
      patch = {
        ...patch,
        data_inicio:          novoInicio,
        data_fim:             novoFim,
        data_inicio_prevista: novoInicio,
        data_fim_prevista:    novoFim,
      };
      // (forced=true significa que o clamp veio de dependência — o usuário
      // pediu depende_de e vamos honrar mesmo que digite datas fora da regra.)
      void forced;
    }

    const usuario = opts.usuario_nome || "Sistema";
    const alteradoEm = new Date().toISOString();
    // Snapshot dos valores anteriores para permitir Desfazer (undo).
    // Contém a entrada principal + todas as descendentes recalculadas.
    const snapshot = [];
    const entradas = [];

    for (const campo of CAMPOS_HISTORICO_LOCAL) {
      if (!(campo in patch)) continue;
      const antes = atual[campo];
      const depois = patch[campo];
      // Normaliza null/undefined/"" como equivalentes (estado vazio) para não gerar entrada espúria.
      const norm = (v) => (v === null || v === undefined || v === "" ? null : v);
      const a1 = norm(antes);
      const b1 = norm(depois);
      if (JSON.stringify(a1) === JSON.stringify(b1)) continue;
      entradas.push({
        id: genId("hist"),
        atividade_id: id,
        campo,
        valor_anterior: antes,
        valor_novo: depois,
        alterado_em: alteradoEm,
        usuario_nome: usuario,
        tipo_alteracao: "edicao",
      });
    }

    dispatchSync({ type: "ATIVIDADE_UPDATE", payload: { id, ...patch, updated_at: alteradoEm } });
    if (entradas.length > 0) {
      dispatch({ type: "ATIVIDADE_HIST_BULK", payload: entradas });
    }

    // Snapshot da atividade principal (só campos do patch)
    const prevMain = {};
    for (const k of Object.keys(patch)) prevMain[k] = atual[k];
    snapshot.push({ id, prev: prevMain, atividade_numero: atual.numero, atividade_titulo: atual.titulo });

    // -------- Recálculo determinístico em cascata --------
    // Se as datas mudaram (data_inicio/data_fim ou os espelhos previstos),
    // percorre topologicamente todos os dependentes transitivos e recalcula
    // suas datas: data_inicio = próximo dia útil após max(data_fim dos preds);
    //             data_fim    = calcDataFim(inicio, horas_estimadas).
    // Cascata dispara quando o clamp recalculou as datas via dependência OU
    // quando o próprio início/fim mudou por edição manual (mesmo sem clamp) —
    // nesse caso os sucessores ainda precisam enxergar o novo fim/início
    // desta atividade. Antes comparava patch×atual campo-a-campo, o que, com
    // o canônico/espelho divergidos dos dados antigos, disparava a cascata
    // (e colapsava dependentes) mesmo num save inócuo.
    const datasMudaram = clampNeeded || _inicioMudou || _fimMudou;
    let cascataAlteracoes = 0;
    if (datasMudaram) {
      const projetoId = atual.projeto_id;
      // Snapshot mutável do estado com o patch aplicado (Map id → atividade)
      const mutState = new Map();
      for (const t of state.atividades) {
        if (t.projeto_id !== projetoId) continue;
        mutState.set(t.id, { ...t });
      }
      if (mutState.has(id)) Object.assign(mutState.get(id), patch);

      // Ordenação topológica (Kahn) sobre o subgrafo do projeto
      const graf = new Map();       // predId -> Set(succIds)
      const inDeg = new Map();      // succId -> in-degree count
      for (const [tid] of mutState) { graf.set(tid, new Set()); inDeg.set(tid, 0); }
      for (const [tid, t] of mutState) {
        for (const dep of (t.depende_de || [])) {
          if (!graf.has(dep)) continue;
          graf.get(dep).add(tid);
          inDeg.set(tid, (inDeg.get(tid) || 0) + 1);
        }
      }
      const queue = [];
      for (const [tid, d] of inDeg) { if (d === 0) queue.push(tid); }
      const ordem = [];
      while (queue.length) {
        const cur = queue.shift();
        ordem.push(cur);
        for (const nx of graf.get(cur) || []) {
          inDeg.set(nx, inDeg.get(nx) - 1);
          if (inDeg.get(nx) === 0) queue.push(nx);
        }
      }
      // (Se houver ciclo, ordem incompleta — apenas ignoramos os cíclicos.)

      // Identifica atividades no fecho transitivo descendente de `id`
      const descendentes = new Set();
      {
        const fila = [id];
        while (fila.length) {
          const cur = fila.shift();
          for (const nx of graf.get(cur) || []) {
            if (!descendentes.has(nx)) { descendentes.add(nx); fila.push(nx); }
          }
        }
      }

      const cascataEntradas = [];
      // Percorre em ordem topológica; para cada descendente, recalcula datas
      for (const tid of ordem) {
        if (!descendentes.has(tid)) continue;
        const dep = mutState.get(tid);
        // maxFim entre os fim das próprias predecessoras
        const preds = (dep.depende_de || []).map(pid => mutState.get(pid)).filter(Boolean);
        if (preds.length === 0) continue;
        const maxFim = preds.reduce((mx, p) => {
          const f = p.data_fim || p.data_fim_prevista || "";
          return f > mx ? f : mx;
        }, "");
        if (!maxFim) continue;
        const novoInicio = proximoDiaUtil(maxFim);
        // (c) Preserva a duração calendárica ATUAL do dependente (delta
        // início→fim) ao empurrá-lo. Antes usava calcDataFim(horas): como
        // atividades importadas do V360 não têm `horas_estimadas`, o fim
        // colapsava para o próprio início (barra de largura 0) e a atividade
        // "sumia" do Gantt. Só caímos para calcDataFim quando não há duração válida.
        let novoFim;
        const _ini = dep.data_inicio || dep.data_inicio_prevista;
        const _fim = dep.data_fim || dep.data_fim_prevista;
        const _deltaMs = (_ini && _fim) ? (new Date(_fim).getTime() - new Date(_ini).getTime()) : -1;
        if (_deltaMs >= 0) {
          novoFim = new Date(new Date(novoInicio).getTime() + _deltaMs).toISOString().slice(0, 10);
        } else {
          novoFim = calcDataFim(novoInicio, dep.horas_estimadas);
        }
        // Sem mudança real? pula.
        if (dep.data_inicio === novoInicio && dep.data_fim === novoFim) continue;

        const patchCascata = {
          data_inicio: novoInicio,
          data_fim:    novoFim,
          // Espelha nos campos "previstos" para manter SB-06 ↔ Gantt legado consistentes
          data_inicio_prevista: novoInicio,
          data_fim_prevista:    novoFim,
        };
        // Log granular por campo alterado
        for (const [campo, novo] of Object.entries(patchCascata)) {
          const anterior = dep[campo];
          if (anterior === novo) continue;
          cascataEntradas.push({
            id: genId("hist"),
            atividade_id: tid,
            campo,
            valor_anterior: anterior,
            valor_novo: novo,
            alterado_em: alteradoEm,
            usuario_nome: usuario,
            tipo_alteracao: "recalculo_cascata",
            observacao: `Recalculado automaticamente após alteração de datas em #${atual.numero || id}.`,
          });
        }
        // Aplica no mutState (para descendentes subsequentes)
        // Antes de aplicar, captura os valores originais para o undo
        const prevCascata = {};
        for (const k of Object.keys(patchCascata)) prevCascata[k] = dep[k];
        snapshot.push({
          id: tid, prev: prevCascata,
          atividade_numero: dep.numero, atividade_titulo: dep.titulo,
          via_cascata: true,
        });
        Object.assign(dep, patchCascata);
        cascataAlteracoes += 1;
        dispatchSync({ type: "ATIVIDADE_UPDATE", payload: { id: tid, ...patchCascata, updated_at: alteradoEm } });
      }
      if (cascataEntradas.length > 0) {
        dispatch({ type: "ATIVIDADE_HIST_BULK", payload: cascataEntradas });
      }
    }

    return { ok: true, alteracoes: entradas.length, cascata: cascataAlteracoes, snapshot };
  }, [state.atividades, computeDepsAdjustedDates]);

  /**
   * Out/2026 — Reposicionamento manual da coluna "#" (SB-06).
   *
   * Deliberadamente SÍNCRONO/AWAITED — diferente do padrão fire-and-forget
   * usado pelos outros campos (`updateAtividadeSB06`). Reposicionar desloca
   * VÁRIAS atividades em cascata de uma só vez; se tratássemos como
   * "otimista local + grava em segundo plano", uma hidratação concorrente
   * poderia reverter a ordem silenciosamente — mesma classe de bug já
   * corrigida nesta sessão para datas e dependências. Em vez disso:
   *   1. Chama o endpoint dedicado e ESPERA a confirmação do servidor
   *      (transação atômica — ou desloca tudo, ou nada).
   *   2. Aplica no estado local EXATAMENTE o que o servidor confirmou
   *      (`ordem_sequencial` de cada atividade afetada) — zero recálculo
   *      próprio, zero chance de divergência tela×banco.
   *   3. O servidor já bump `updated_at` de cada linha afetada na MESMA
   *      transação — isso aciona a proteção de merge já existente
   *      (`isNewer`) contra uma hidratação concorrente desatualizada.
   * Lança erro (com mensagem amigável) em caso de posição fora do
   * intervalo ou violação de predecessora/sucessora — o chamador (form)
   * deve exibir isso ao usuário e NÃO prosseguir com o resto do save.
   */
  const reordenarAtividade = useCallback(async (id, novaPosicao, opts = {}) => {
    const atual = state.atividades.find(t => t.id === id);
    if (!atual) throw new Error("Atividade não encontrada.");
    if (String(id).startsWith("ati-")) {
      throw new Error("Aguarde esta atividade terminar de sincronizar com o servidor antes de reposicionar.");
    }
    if (novaPosicao === atual.numero) return [];
    let resp;
    try {
      resp = await sprints360Api.atividades.reordenar(id, novaPosicao);
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Falha ao reposicionar a atividade.";
      throw new Error(msg);
    }
    const atualizadas = resp?.atividades || [];
    const porId = new Map(atualizadas.map(a => [
      a.id, { id: a.id, numero: a.ordem_sequencial, updated_at: a.updated_at },
    ]));
    if (porId.size > 0) {
      dispatch({ type: "ATIVIDADE_REORDER_BULK", payload: Array.from(porId.values()) });
    }
    const numeroNovo = porId.get(id)?.numero ?? novaPosicao;
    if (numeroNovo !== atual.numero) {
      dispatch({ type: "ATIVIDADE_HIST_ADD", payload: {
        id: genId("hist"), atividade_id: id, campo: "numero",
        valor_anterior: atual.numero, valor_novo: numeroNovo,
        alterado_em: new Date().toISOString(),
        usuario_nome: opts.usuario_nome || "Sistema",
        tipo_alteracao: "edicao",
      }});
    }
    return atualizadas;
  }, [state.atividades, genId]);


  /**
   * SB-06 revert — desfaz um conjunto de alterações capturadas por
   * `updateAtividadeSB06`. Aplica os valores anteriores em cada atividade
   * do snapshot, gera entradas em `atividade_historico` com
   * `tipo_alteracao="undo"` para auditoria.
   */
  const revertAtividades = useCallback((snapshot, opts = {}) => {
    if (!Array.isArray(snapshot) || snapshot.length === 0) return { ok: false };
    const usuario = opts.usuario_nome || "Sistema";
    const alteradoEm = new Date().toISOString();
    const entradas = [];
    for (const { id, prev } of snapshot) {
      dispatchSync({ type: "ATIVIDADE_UPDATE", payload: { id, ...prev, updated_at: alteradoEm } });
      for (const [campo, valor] of Object.entries(prev)) {
        entradas.push({
          id: genId("hist"), atividade_id: id, campo,
          valor_anterior: null, valor_novo: valor,
          alterado_em: alteradoEm, usuario_nome: usuario,
          tipo_alteracao: "undo",
          observacao: "Alteração desfeita (undo).",
        });
      }
    }
    if (entradas.length > 0) dispatch({ type: "ATIVIDADE_HIST_BULK", payload: entradas });
    return { ok: true, revertidas: snapshot.length };
  }, []);

  /**
   * SB-06 delete. Regras:
   *  - Bloqueia se houver dependentes (sucessoras). Retorna a lista.
   *  - Se a atividade estiver em sprint FECHADO (v14 finalizada da versão-mãe),
   *    faz cancelamento LÓGICO (`ativo=false`, `status='cancelado'`), preservando
   *    integridade do relatório de fechamento.
   *  - Caso contrário, delete físico + entrada de histórico do tipo "exclusao".
   */
  const deleteAtividadeSB06 = useCallback((id, opts = {}) => {
    const atual = state.atividades.find(t => t.id === id);
    if (!atual) return { ok: false, erro: "Atividade não encontrada." };

    const dependentes = state.atividades.filter(t => Array.isArray(t.depende_de) && t.depende_de.includes(id));
    if (dependentes.length > 0 && !opts.forcar) {
      return {
        ok: false,
        motivo: "com_dependentes",
        dependentes: dependentes.map(d => ({ id: d.id, titulo: d.titulo, numero: d.numero })),
        erro: `Não é possível excluir: ${dependentes.length} atividade(s) dependem desta.`,
      };
    }

    // Checagem de sprint fechado (versão finalizada + sprint pertence a essa versão)
    const versaoDaAtividade = atual.versao_id ? state.versoes.find(v => v.id === atual.versao_id) : null;
    const sprintFechado = versaoDaAtividade && versaoDaAtividade.status === "finalizado";

    const usuario = opts.usuario_nome || "Sistema";
    const alteradoEm = new Date().toISOString();

    if (sprintFechado) {
      // Cancelamento lógico
      dispatchSync({ type: "ATIVIDADE_UPDATE", payload: {
        id, ativo: false, status: "cancelado", updated_at: alteradoEm,
      }});
      dispatch({ type: "ATIVIDADE_HIST_ADD", payload: {
        id: genId("hist"), atividade_id: id, campo: "status",
        valor_anterior: atual.status, valor_novo: "cancelado",
        alterado_em: alteradoEm, usuario_nome: usuario,
        tipo_alteracao: "cancelamento_logico",
        observacao: "Cancelamento lógico — atividade estava em versão finalizada.",
      }});
      return { ok: true, tipo: "cancelamento_logico" };
    }

    dispatch({ type: "ATIVIDADE_HIST_ADD", payload: {
      id: genId("hist"), atividade_id: id, campo: "__exclusao__",
      valor_anterior: atual.titulo, valor_novo: null,
      alterado_em: alteradoEm, usuario_nome: usuario,
      tipo_alteracao: "exclusao",
    }});
    dispatchSync({ type: "ATIVIDADE_DEL", id });
    return { ok: true, tipo: "exclusao" };
  }, [state.atividades, state.versoes]);

  // ---------------------------------------------------------------
  // Phase 12.61 · revalidarCronograma(projetoId)
  // ---------------------------------------------------------------
  // Faz um recálculo topológico das datas de TODAS as atividades do
  // projeto, respeitando as dependências (`depende_de`).
  //   - Para cada atividade com predecessoras: `data_inicio` = próximo
  //     dia útil após o max(`data_fim`) das preds; `data_fim` recalculado
  //     preservando a duração calendárica já digitada pelo usuário (mesma
  //     regra da cascata do `updateAtividadeSB06` — só cai para
  //     `calcDataFim(horas_estimadas)` quando não há duração válida).
  //   - Atividades sem preds, ou cujas datas já estão corretas, não mudam
  //     (sem escrita/toast desnecessário).
  //
  // Out/2026 — chamado explicitamente após um reposicionamento manual
  // (drag & drop) bem-sucedido em `reordenarAtividade`, para que mover uma
  // atividade antes/depois de outra também corrija datas de sucessoras que
  // ficaram descoladas das predecessoras. NÃO é mais chamado automaticamente
  // no mount do Cronograma (ver Phase 12.55.19) — aquela remoção tratava de
  // um bug diferente: sobrescrever `data_fim` via `calcDataFim(horas)`
  // perdendo a duração digitada pelo usuário. Esse cálculo agora preserva o
  // delta calendárico, então uma chamada explícita (gatilho do usuário) é
  // segura.
  const revalidarCronograma = useCallback((projetoId, opts = {}) => {
    if (!projetoId) return { ok: false, motivo: "sem_projeto" };
    const ativsProj = state.atividades.filter(a => a.projeto_id === projetoId);
    if (ativsProj.length === 0) return { ok: true, alteracoes: 0, cascata: 0, snapshot: [] };

    // Snapshot mutável
    const mut = new Map();
    for (const a of ativsProj) mut.set(a.id, { ...a });

    // Grafo + Kahn
    const succ = new Map();
    const inDeg = new Map();
    for (const [tid] of mut) { succ.set(tid, new Set()); inDeg.set(tid, 0); }
    for (const [tid, a] of mut) {
      for (const dep of (a.depende_de || [])) {
        if (!mut.has(dep)) continue;
        succ.get(dep).add(tid);
        inDeg.set(tid, (inDeg.get(tid) || 0) + 1);
      }
    }
    const queue = [];
    for (const [tid, d] of inDeg) if (d === 0) queue.push(tid);
    const ordem = [];
    while (queue.length) {
      const cur = queue.shift();
      ordem.push(cur);
      for (const nx of succ.get(cur) || []) {
        inDeg.set(nx, inDeg.get(nx) - 1);
        if (inDeg.get(nx) === 0) queue.push(nx);
      }
    }

    const usuario = opts.usuario_nome || "Sistema";
    const alteradoEm = new Date().toISOString();
    const snapshot = [];
    const historEntradas = [];
    let alteracoes = 0;
    for (const tid of ordem) {
      const a = mut.get(tid);
      const preds = (a.depende_de || []).map(pid => mut.get(pid)).filter(Boolean);
      if (preds.length === 0) continue;
      const maxFim = preds.reduce((mx, p) => {
        const f = p.data_fim || p.data_fim_prevista || "";
        return f > mx ? f : mx;
      }, "");
      if (!maxFim) continue;
      const novoInicio = proximoDiaUtil(maxFim);
      const iniAtual = a.data_inicio || a.data_inicio_prevista;
      const fimAtual = a.data_fim || a.data_fim_prevista;
      // Preserva a duração calendárica já digitada (delta início→fim);
      // só cai para calcDataFim(horas) quando não há duração válida.
      const deltaMs = (iniAtual && fimAtual) ? (new Date(fimAtual).getTime() - new Date(iniAtual).getTime()) : -1;
      const novoFim = deltaMs >= 0
        ? new Date(new Date(novoInicio).getTime() + deltaMs).toISOString().slice(0, 10)
        : calcDataFim(novoInicio, a.horas_estimadas);
      if (iniAtual === novoInicio && fimAtual === novoFim) continue;
      const patch = {
        data_inicio: novoInicio,
        data_fim:    novoFim,
        data_inicio_prevista: novoInicio,
        data_fim_prevista:    novoFim,
      };
      for (const [campo, novo] of Object.entries(patch)) {
        const anterior = a[campo];
        if (anterior === novo) continue;
        historEntradas.push({
          id: genId("hist"), atividade_id: tid, campo,
          valor_anterior: anterior, valor_novo: novo,
          alterado_em: alteradoEm, usuario_nome: usuario,
          tipo_alteracao: "recalculo_cascata",
          observacao: "Datas revisadas após reposicionamento (#) do cronograma.",
        });
      }
      const prevCascata = {};
      for (const k of Object.keys(patch)) prevCascata[k] = a[k];
      snapshot.push({ id: tid, prev: prevCascata, atividade_numero: a.numero, atividade_titulo: a.titulo, via_cascata: true });
      Object.assign(a, patch);
      dispatchSync({ type: "ATIVIDADE_UPDATE", payload: { id: tid, ...patch, updated_at: alteradoEm } });
      alteracoes += 1;
    }
    if (historEntradas.length > 0) {
      dispatch({ type: "ATIVIDADE_HIST_BULK", payload: historEntradas });
    }
    return { ok: true, alteracoes, cascata: alteracoes, snapshot };
  }, [state.atividades]);

  const value = useMemo(() => ({
    state, dispatch: dispatchSync, dispatchSync, genId,
    getProjeto, projetoData, getVersoesDoProjeto, getVersaoAtiva, isProjetoRascunho,
    createProjeto, createAtividade, calcDataInicioComDeps,
    createVersao, setVersaoAtiva, updateVersao, toggleStatusVersao,
    toggleStatusCronograma,
    // SB-06
    getDependentesDe, getAtividadeHistorico,
    updateAtividadeSB06, deleteAtividadeSB06, revertAtividades, reordenarAtividade,
    // Phase 12.61 — Cronograma
    revalidarCronograma,
    // Phase 12.9 — Hidratação backend
    hydrated, hydrationError, hydrateFromBackend, promoteProjetoParaBackend, promoteAllLocalProjects,
    // Jun/2026 — Salvar antes de sair
    pendingSyncCount, flushPendingSync,
  }), [state, getProjeto, projetoData, getVersoesDoProjeto, getVersaoAtiva, isProjetoRascunho,
       createProjeto, createAtividade, calcDataInicioComDeps,
       createVersao, setVersaoAtiva, updateVersao, toggleStatusVersao,
       toggleStatusCronograma,
       getDependentesDe, getAtividadeHistorico,
       updateAtividadeSB06, deleteAtividadeSB06, revertAtividades, reordenarAtividade,
       revalidarCronograma,
       hydrated, hydrationError, hydrateFromBackend, dispatchSync,
       promoteProjetoParaBackend, promoteAllLocalProjects,
       pendingSyncCount, flushPendingSync]);

  return <Sprints360Ctx.Provider value={value}>{children}</Sprints360Ctx.Provider>;
}

export function useSprints360() {
  const ctx = useContext(Sprints360Ctx);
  if (!ctx) throw new Error("useSprints360 must be used inside <Sprints360Provider>");
  return ctx;
}
